import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Rating } from 'primereact/rating';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminRowMenuItem,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  AdminMainText,
  AdminMetadataText,
  AdminPanelTitle,
  AdminRowAction,
  AdminRowActionsButton,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  adminFormSelectClasses,
  adminFormTextareaClasses,
  adminWizardSelectClasses,
  withoutEscalatedAuditLogEntries,
  isEscalatedAuditLogEntry,
  getAdminNoteAddedByDisplay,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
  ADMIN_TOOLBAR_CLASS,
} from '../shared/adminUx';
import { DashboardIcon } from '../../../components/common/Dashboard';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { getDetailTablePagination, AUDIT_LOG_DETAIL_GRID } from '../../../utils/configurationTableUx';
import Profile from '../../../assets/img/User.png';
import BusinessFallback from '../../../assets/img/default-image.jpg';
import { getScenarioByCaseId, getArchiveCaseByCaseId } from '../../../data/postModerationScenarios';
import { getProfileScenarioByCaseId } from '../../../data/profileModerationScenarios';
import { getReviewScenarioByCaseId } from '../../../data/reviewModerationScenarios';
import { getMessageScenarioByCaseId } from '../../../data/messageModerationScenarios';
import { getQuoteScenarioByCaseId } from '../../../data/quoteModerationScenarios';
import {
  getModerationCaseOverride,
  patchModerationCaseOverride,
  saveDecidedModerationCase,
} from '../../../utils/moderationCaseSession';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { recordCaseActivity } from '../../../data/changeHistoryAudit';
import { emitPublicationActivity } from '../../../data/changeHistoryEmitters';
import ModerationOverviewTab from './ModerationOverviewTab';
import ModerationDecisionTab from './ModerationDecisionTab';
import { buildModerationOverviewModel } from './buildModerationOverviewModel';
import {
  archiveIdFromQueueCaseId,
  buildCapturedEvidence,
  buildSourceStateFields,
  formatQueueDateTime,
  normalizeQueueCase,
  normalizeQueueStatus,
  normalizeSupportTeam,
  resolveCaseHeader,
} from '../../../data/moderationQueueSpec';
import {
  buildArchiveAuditLog,
  buildArchiveNotes,
  buildArchiveOverviewModel,
  buildArchivedDecision,
  findArchiveCase,
} from '../../../data/moderationArchiveSpec';

const formatOverviewCaseStatus = (s) => {
  if (s == null || s === '') return '—';
  return normalizeQueueStatus(s);
};

const resolveOverviewCaseSource = (flag, item, scenarioKey) => {
  if (item?.caseSource) return item.caseSource;
  const source = String(flag?.source || '').toLowerCase();
  const reason = String(flag?.reason || flag?.trigger || '').toLowerCase();
  if (scenarioKey === 3 && item?.contentType === 'Review') return 'Detection';
  if (scenarioKey === 1 && item?.contentType === 'Message') return 'System Signal';
  if (source.includes('detection') || reason.includes('repeated promotional') || reason.includes('pattern')) {
    return 'Detection';
  }
  if (source === 'system' || source.includes('system')) return 'System Signal';
  return 'User Flag';
};

const formatOverviewDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase();
  return `${datePart}, ${timePart}`;
};

// Single category value; remove " / Gas" from end; "Multiple Trades" → "Electric" (matches Business list)
const formatBusinessCategory = (raw) => {
  if (raw == null || raw === '') return '—';
  const single = Array.isArray(raw) ? raw[0] : (typeof raw === 'string' && raw.includes(',') ? raw.split(',')[0].trim() : raw);
  const str = typeof single === 'string' ? single : String(single);
  if (/^multiple\s+trades?$/i.test(str.trim())) return 'Electric';
  const display = str.replace(/\s*\/\s*Gas\s*$/i, '').trim();
  return display || '—';
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatComplianceNotesDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart}, ${timePart}`;
};

// Format datetime for Admin Notes display: "14 Jan 2026 · 10:42 AM"
const formatNoteDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} · ${timePart}`;
};

// Format date for Decision Record: "14 Jan 2026" (DD MMM YYYY)
const formatDateDDMMMYYYY = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDuration = (ms) => {
  if (!ms || ms < 0) return '—';
  const minutes = Math.floor(ms / (1000 * 60));
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes - days * 24 * 60) / 60);
  const mins = minutes % 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(' ');
};

const guessTypeFromId = (itemId) => {
  if (!itemId) return 'Flag';
  if (itemId.startsWith('POST#')) return 'Posts';
  if (itemId.startsWith('RV#')) return 'Reviews';
  if (itemId.startsWith('MSG#')) return 'Messages';
  if (itemId.startsWith('BP#')) return 'Business Profiles';
  if (itemId.startsWith('CP#')) return 'Customer Profiles';
  if (itemId.startsWith('DOC#')) return 'Documents & Licences';
  if (itemId.startsWith('MC#')) return 'Misconduct';
  if (itemId.startsWith('FLAG#')) return 'System Flags';
  return 'Flag';
};

// Helper function to generate content based on item type
const generateContentByType = (type, id) => {
    // Create a simple numeric hash from ID for consistent image seeds
    const hashId = id ? String(id).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 12345;
    const baseSeed = hashId % 10000; // Keep it within reasonable range
    
    const baseContent = {
      gallery: [
        { url: `https://picsum.photos/seed/${baseSeed}1/640/400`, flagged: false },
        { url: `https://picsum.photos/seed/${baseSeed}2/640/400`, flagged: true },
        { url: `https://picsum.photos/seed/${baseSeed}3/640/400`, flagged: false },
        { url: `https://picsum.photos/seed/${baseSeed}4/640/400`, flagged: false },
      ],
      attachments: [
        { name: 'screenshot-1.png', url: `https://picsum.photos/seed/${baseSeed}5/800/600`, type: 'screenshot', size: '1.2 MB' },
        { name: 'screenshot-2.png', url: `https://picsum.photos/seed/${baseSeed}6/800/600`, type: 'screenshot', size: '0.9 MB' },
      ],
    };

    switch (type) {
      case 'Posts':
        return {
          ...baseContent,
          postText: 'Premium Bathroom Renovation Specialists Needed. Transform your bathroom with our certified team. We deliver premium finishes, custom cabinetry, waterproofing, tiling, and fixture installation. We handle council approvals and provide detailed quotations upfront.',
        };
      case 'Reviews':
        return {
          ...baseContent,
          reviewText: 'This business provided excellent service. The quality of work was outstanding and they completed the job on time. Highly recommend!',
        };
      case 'Messages':
        return {
          ...baseContent,
          messageThread: [
            { sender: 'Customer', timestamp: '2025-01-15 10:30', text: 'Hello, I need help with my order.' },
            { sender: 'Business', timestamp: '2025-01-15 10:35', text: 'Hi, I can help you with that. What seems to be the issue?' },
            { sender: 'Customer', timestamp: '2025-01-15 10:40', text: 'The service was not as described.' },
          ],
        };
      case 'Business Profiles':
        return {
          ...baseContent,
          businessFields: {
            businessName: 'Northside Renovations',
            description: 'Professional renovation services',
            category: 'Renovation',
            location: 'Doncaster, VIC',
            contactEmail: 'contact@northside.com',
            phone: '+61 400 123 456',
          },
        };
      case 'Customer Profiles':
        return {
          ...baseContent,
          customerFields: {
            customerName: 'John Smith',
            email: 'john.smith@example.com',
            phone: '+61 400 789 012',
            location: 'Melbourne, VIC',
          },
        };
      case 'Documents & Licences':
        return {
          ...baseContent,
          documents: [
            { name: 'Business Licence.pdf', type: 'licence', uploadedDate: '2025-01-10', status: 'Pending Review' },
            { name: 'Insurance Certificate.pdf', type: 'insurance', uploadedDate: '2025-01-12', status: 'Approved' },
          ],
        };
      case 'Misconduct':
        return {
          ...baseContent,
          misconductReport: {
            description: 'Reported misconduct incident details...',
            incidentDate: '2025-01-14',
            location: 'Melbourne, VIC',
          },
        };
      default:
        return baseContent;
    }
};

// Helper function to generate flag information based on flag type
const generateFlagInfo = (flagType, id) => {
  if (!flagType) {
    // Default to system flag
    return {
      type: 'system',
      flagType: 'System Flag — Rules Engine',
      ruleId: 'RL-001',
      ruleName: 'Content Review Required',
      ruleCategory: 'Content Moderation',
      ruleDescription: 'Content flagged for review by automated system.',
      triggerData: 'Content contains potential violations',
      triggerSource: 'Content Moderation Module',
      triggeredOn: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      repeatFlagCount: 0,
    };
  }
  
  if (flagType === 'System Flag — Rules Engine' || flagType.includes('System Flag')) {
      return {
        type: 'system',
        flagType: 'System Flag — Rules Engine',
        ruleId: 'RL-032',
        ruleName: 'Licence Expired',
        ruleCategory: 'Compliance — Documents',
        ruleDescription: 'Flags when licence expiry has passed.',
        triggerData: 'Licence expiry: 12 Aug 2024',
        triggerSource: 'Business Verification Module',
        triggeredOn: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        repeatFlagCount: 2,
      };
    } else if (flagType.includes('User Report')) {
      const isCustomer = flagType.includes('Customer');
      return {
        type: 'user',
        reportType: isCustomer ? 'Customer Report' : 'Business Report',
        reporterId: isCustomer ? 'CUS#11832' : 'BUS#4521',
        reporterName: isCustomer ? 'John D.' : 'Sarah M.',
        reportCategory: 'Abusive Behaviour',
        reportReason: 'Offensive message sent.',
        reportNotes: 'Called me a scammer.',
        evidenceProvided: 'Yes — screenshot',
        reportTimestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        previousReports: 3,
      };
    } else if (flagType.includes('Admin Flag') || flagType.includes('Manual Admin Flag')) {
      return {
        type: 'admin',
        flaggedBy: 'Admin#44 — "Michael T."',
        flagCategory: 'Verification Check Required',
        flagReason: 'ABN mismatch with profile name.',
        flagNotes: 'Request new certificate.',
        flagTimestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      };
    } else if (flagType.includes('Licence') || flagType.includes('Document')) {
      return {
        type: 'system',
        flagType: 'System Flag — Rules Engine',
        ruleId: 'RL-045',
        ruleName: 'Document Pending Review',
        ruleCategory: 'Compliance — Documents',
        ruleDescription: 'Flags when document requires manual review.',
        triggerData: 'Document uploaded: Business Licence.pdf',
        triggerSource: 'Document Verification Module',
        triggeredOn: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        repeatFlagCount: 0,
      };
    } else if (flagType.includes('Misconduct')) {
      return {
        type: 'user',
        reportType: 'Customer Report',
        reporterId: 'CUS#12045',
        reporterName: 'Jane S.',
        reportCategory: 'Misconduct Report',
        reportReason: 'Reported misconduct incident.',
        reportNotes: 'Incident occurred during service delivery.',
        evidenceProvided: 'Yes — photos and statement',
        reportTimestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        previousReports: 1,
      };
    }
    // Default to system flag
    return {
      type: 'system',
      flagType: 'System Flag — Rules Engine',
      ruleId: 'RL-001',
      ruleName: 'Content Review Required',
      ruleCategory: 'Content Moderation',
      ruleDescription: 'Content flagged for review by automated system.',
      triggerData: 'Content contains potential violations',
      triggerSource: 'Content Moderation Module',
      triggeredOn: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      repeatFlagCount: 0,
    };
};

// Helper function to generate sample notes
const generateNotes = (id) => {
  const now = new Date();
  const formatDateTimeForNote = (date) => {
    return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  };
  
  return [
    {
      id: 'note-1',
      timestamp: formatDateTimeForNote(new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000)),
      createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      date: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      noteContent: 'Content reviewed contains strong language but no direct threats or harassment. Context suggests frustration. Recommend warning rather than removal.',
      content: 'Content reviewed contains strong language but no direct threats or harassment. Context suggests frustration. Recommend warning rather than removal.'
    },
    {
      id: 'note-2',
      timestamp: formatDateTimeForNote(new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)),
      createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      date: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      noteContent: 'Case closed after warning issued. No further reports received since action taken.',
      content: 'Case closed after warning issued. No further reports received since action taken.'
    },
    {
      id: 'note-3',
      timestamp: formatDateTimeForNote(new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000)),
      createdAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      date: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      noteContent: 'Item reviewed for compliance. Content appears to be within guidelines. No action needed at this time.',
      content: 'Item reviewed for compliance. Content appears to be within guidelines. No action needed at this time.'
    },
    {
      id: 'note-4',
      timestamp: formatDateTimeForNote(new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)),
      createdAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      date: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      noteContent: 'Content reviewed and flagged for review. Additional verification required before proceeding.',
      content: 'Content reviewed and flagged for review. Additional verification required before proceeding.'
    },
    {
      id: 'note-5',
      timestamp: formatDateTimeForNote(new Date(now.getTime() - 12 * 60 * 60 * 1000)),
      createdAt: new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString(),
      date: new Date(now.getTime() - 12 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      noteContent: 'Security check completed. No suspicious activity detected. Case cleared for normal processing.',
      content: 'Security check completed. No suspicious activity detected. Case cleared for normal processing.'
    },
    {
      id: 'note-6',
      timestamp: formatDateTimeForNote(new Date(now.getTime() - 6 * 60 * 60 * 1000)),
      createdAt: new Date(now.getTime() - 6 * 60 * 60 * 1000).toISOString(),
      date: new Date(now.getTime() - 6 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      author: 'John Smith',
      adminName: 'Admin — John Smith (Moderation)',
      email: 'admin@tradesposter.com',
      noteContent: 'Technical issue identified during review. Referred to development team for investigation.',
      content: 'Technical issue identified during review. Referred to development team for investigation.'
    },
    {
      id: 'note-7',
      timestamp: formatDateTimeForNote(new Date(now.getTime() - 2 * 60 * 60 * 1000)),
      createdAt: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(),
      date: new Date(now.getTime() - 2 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      category: 'Other',
      noteContent: 'Follow-up note: Customer has been contacted regarding the issue. Awaiting response.',
      content: 'Follow-up note: Customer has been contacted regarding the issue. Awaiting response.'
    },
    {
      id: 'note-8',
      timestamp: formatDateTimeForNote(new Date(now.getTime() - 30 * 60 * 1000)),
      createdAt: new Date(now.getTime() - 30 * 60 * 1000).toISOString(),
      date: new Date(now.getTime() - 30 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      author: 'Sarah Johnson',
      adminName: 'Admin — Sarah Johnson (Moderation)',
      email: 'admin@tradesposter.com',
      noteContent: 'Final review completed. All checks passed. Case can be closed.',
      content: 'Final review completed. All checks passed. Case can be closed.'
    },
  ];
};

const ModerationItemDetail = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const itemFromState = location.state?.item;
  const isArchive = location.state?.readOnly === true;
  const scenario = useMemo(
    () =>
      getScenarioByCaseId(itemId || itemFromState?.caseId) ||
      getProfileScenarioByCaseId(itemId || itemFromState?.caseId) ||
      getReviewScenarioByCaseId(itemId || itemFromState?.caseId) ||
      getMessageScenarioByCaseId(itemId || itemFromState?.caseId) ||
      getQuoteScenarioByCaseId(itemId || itemFromState?.caseId),
    [itemId, itemFromState?.caseId]
  );
  const isProfileScenario = Boolean(scenario?.profileType);
  const isReviewScenario = Boolean(scenario?.review);
  const isMessageScenario = Boolean(scenario?.conversation);
  const isQuoteScenario = Boolean(scenario?.quote);

  // Simulated data model for the detail view (no content, admin metadata only)
  const now = useMemo(() => new Date(), []);
  const [activeTab, setActiveTab] = useState('overview');
  const [previewMedia, setPreviewMedia] = useState(null);
  const [archiveCaseState, setArchiveCaseState] = useState({});

  useEffect(() => {
    const tabFromState = location.state?.activeTab;
    const allowed = isArchive
      ? ['overview', 'content', 'enforcement', 'notes', 'logs']
      : ['overview', 'content', 'enforcement', 'notes', 'logs'];
    if (tabFromState && allowed.includes(tabFromState)) {
      setActiveTab(tabFromState);
    }
  }, [location.state?.activeTab, isArchive]);

  // Admin Notes state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({
    noteText: ''
  });
  const [appealForm, setAppealForm] = useState({
    requestedBy: '',
    appealSummary: '',
  });
  
  // Logs filters state (same as CustomerDetail.js)
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [actorOpen, setActorOpen] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  
  // Filter values
  const [filters, setFilters] = useState({
    dateRange: 'All',
    actor: 'All',
    module: 'All',
    outcome: 'All'
  });
  
  // Refs for click outside
  const dateRangeRef = useRef(null);
  const actorRef = useRef(null);
  const moduleRef = useRef(null);
  const outcomeRef = useRef(null);
  
  // Click outside handlers
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));
  useClickOutside(actorRef, () => setActorOpen(false));
  useClickOutside(moduleRef, () => setModuleOpen(false));
  useClickOutside(outcomeRef, () => setOutcomeOpen(false));

  // Handle clicking outside to close all filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const filterRefs = [dateRangeRef, actorRef, moduleRef, outcomeRef];
      const isOutsideAll = filterRefs.every(ref => {
        const el = ref?.current;
        return !el || !el.contains(event.target);
      });

      if (isOutsideAll) {
        setDateRangeOpen(false);
        setActorOpen(false);
        setModuleOpen(false);
        setOutcomeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Filter change handler
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };
  const generateDetailFromItem = (item, itemIdParam) => {
    // Use item data from location.state if available, otherwise generate
    const actualItemId = item?.caseId || item?.itemId || itemIdParam || 'MOD-000001';
    let type = item?.type || item?.contentType || guessTypeFromId(actualItemId);
    if (type === 'Message') type = 'Messages';
    const receivedAt = item?.created ? new Date(item.created) : (item?.dateReceived ? new Date(item.dateReceived) : new Date());
    if (!item?.created && !item?.dateReceived) {
      receivedAt.setDate(receivedAt.getDate() - Math.floor(Math.random() * 10) - 1);
    }
    const firstViewedAt = new Date(receivedAt.getTime() + 2 * 60 * 60 * 1000);
    const lastUpdatedAt = item?.updated ? new Date(item.updated) : new Date(receivedAt.getTime() + 6 * 60 * 60 * 1000);
    
    // Use flag type from item if available
    let flagType = item?.source || item?.flagSource || 'System-Flagged';
    if (!item?.source && !item?.flagSource) {
      if (type === 'Review' || type === 'Messages' || type === 'Message') {
        flagType = 'User-Reported';
      } else if (type === 'Quote') {
        flagType = 'User-Reported';
      } else if (type === 'Document') {
        flagType = 'System-Flagged';
      } else if (type === 'Business Profile' || type === 'Customer Profile') {
        flagType = 'Admin-Escalated';
      }
    }

    const rawStatus = item?.status || 'New';
    const normalizedStatus = normalizeQueueStatus(rawStatus);
    return {
      itemId: actualItemId,
      caseId: actualItemId,
      itemType: type,
      type: type,
      thumbnail: item?.thumbnail || `https://picsum.photos/seed/${actualItemId}/128/128`,
      severity: item?.severity || 'Medium',
      flagType: flagType,
      source: flagType,
      queueStatus: normalizedStatus,
      status: normalizedStatus,
      assignedAdmin: item?.assignedTo || 'Unassigned',
      assignedTo: item?.assignedTo || 'Unassigned',
      admins: ['Unassigned', 'John Smith', 'Sarah L.', 'Mike Davis', 'Emma Wilson'],
      receivedAt: receivedAt.toISOString(),
      created: receivedAt.toISOString(),
      firstViewedAt: firstViewedAt.toISOString(),
      lastUpdatedAt: lastUpdatedAt.toISOString(),
      updated: lastUpdatedAt.toISOString(),
      violation: item?.violation || 'SPAM',
      escalationLevel: item?.escalationLevel || 'None',
      escalationState: item?.escalationState || 'Not Escalated',
      reporterType: flagType === 'System-Flagged' ? 'System' : (flagType === 'User-Reported' ? 'Customer' : 'Admin'),
      reporterId: flagType === 'System-Flagged' ? '' : 'U-21994',
      reportReason: type.includes('Review') ? 'Inappropriate language' : '',
      contextSummary: '',
      // Content based on item type
      content: generateContentByType(type, actualItemId),
      // Flag information based on flag type
      flagInfo: generateFlagInfo(flagType, actualItemId),
      // Admin Notes
      notes: generateNotes(actualItemId),
    };
  };

  const [detail, setDetail] = useState(() => generateDetailFromItem(itemFromState, itemId));

  // Update detail when item data or itemId changes
  React.useEffect(() => {
    setDetail(generateDetailFromItem(itemFromState, itemId));
  }, [itemFromState, itemId]);

  // Notes handlers (same as CustomerDetail.js)

  const [flagsCurrentPage, setFlagsCurrentPage] = useState(1);

  useEffect(() => {
    setFlagsCurrentPage(1);
  }, [itemId]);

  // Flags row actions state
  const [flagsRowActionsOpen, setFlagsRowActionsOpen] = useState({});
  const flagsRowActionsRefs = useRef({});
  const [viewFlagModalOpen, setViewFlagModalOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const viewFlagModalRef = useRef(null);

  // Enforcement row actions state
  const [enforcementRowActionsOpen, setEnforcementRowActionsOpen] = useState({});
  const enforcementRowActionsRefs = useRef({});

  const [overviewAssignment, setOverviewAssignment] = useState({
    priority: 'Medium',
    assignedHandler: 'Unassigned',
    caseStatus: 'New',
  });
  const [queueAuditLog, setQueueAuditLog] = useState([]);
  const [queueNotes, setQueueNotes] = useState([]);
  const [noteEditor, setNoteEditor] = useState(null);
  const [viewNote, setViewNote] = useState(null);

  const queueCase = useMemo(
    () => normalizeQueueCase(itemFromState || {}, scenario) || itemFromState || {},
    [itemFromState, scenario]
  );
  const archiveCase = useMemo(
    () => findArchiveCase(itemId || itemFromState?.caseId) || itemFromState || {},
    [itemId, itemFromState]
  );

  useEffect(() => {
    const caseKey = itemFromState?.caseId || scenario?.case?.caseId || itemId;
    const session = getModerationCaseOverride(caseKey);
    const assignedHandler = normalizeSupportTeam(
      session?.supportTeam ||
        itemFromState?.supportTeam ||
        itemFromState?.assignedTo ||
        scenario?.case?.assignedTo
    );
    let caseStatus = session?.status || formatOverviewCaseStatus(
      itemFromState?.status || scenario?.case?.status || 'New'
    );
    caseStatus = normalizeQueueStatus(caseStatus) || caseStatus;
    // New = unassigned. In Progress requires an assigned handler.
    if (assignedHandler !== 'Unassigned' && caseStatus === 'New') {
      caseStatus = 'In Progress';
    } else if (assignedHandler === 'Unassigned' && caseStatus === 'In Progress') {
      caseStatus = 'New';
    }
    let lastUpdated = session?.lastUpdated || itemFromState?.updated || scenario?.case?.updated;
    let sessionAuditEvents = [...(session?.auditEvents || [])];
    // Opening a case is view only. Assignment happens via Assign to Me or manager Assign/Reassign.
    // Unable to Determine is a finding only — it is not a Queue status.

    setOverviewAssignment({
      priority: itemFromState?.priority || scenario?.case?.severity || 'Medium',
      assignedHandler,
      caseStatus,
      lastUpdated,
    });
    const receivedAt = itemFromState?.created || scenario?.case?.created || scenario?.flag?.createdAt;
    const receivedBy = assignedHandler !== 'Unassigned' ? assignedHandler : 'System';
    setQueueAuditLog([
      {
        dateTime: receivedAt,
        event: 'Case receipt',
        details: `${receivedBy === 'System' ? 'System' : receivedBy}; case entered Queue`,
        relatedRecord: itemFromState?.caseId || scenario?.case?.caseId || '—',
      },
      ...sessionAuditEvents,
    ]);
    if (isArchive) {
      setQueueNotes(buildArchiveNotes(archiveCase, scenario));
      setQueueAuditLog(buildArchiveAuditLog(archiveCase, scenario));
      setOverviewAssignment({
        assignedHandler: archiveCase.supportTeam || assignedHandler,
        caseStatus: 'Decided',
        lastUpdated: archiveCase.finalisedOn,
      });
    } else if (Array.isArray(session?.notes)) {
      setQueueNotes(session.notes);
    } else if (scenario?.adminNote) {
      setQueueNotes([
        {
          id: 'scenario-note',
          text: scenario.adminNote,
          addedBy: assignedHandler !== 'Unassigned' ? assignedHandler : 'Alex Chen',
          addedOn: scenario?.case?.updated || scenario?.case?.created || new Date().toISOString(),
        },
      ]);
    } else {
      setQueueNotes([]);
    }
  }, [
    scenario?.case?.caseId,
    scenario?.case?.assignedTo,
    scenario?.case?.status,
    scenario?.adminNote,
    itemFromState?.caseId,
    itemFromState?.supportTeam,
    itemFromState?.assignedTo,
    itemFromState?.status,
    itemFromState?.created,
    itemId,
    isArchive,
    archiveCase?.caseId,
  ]);

  const appendQueueAudit = (entry) => {
    const full = { relatedRecord: queueCase.caseId || '—', ...entry };
    setQueueAuditLog((prev) => [...prev, full]);
    const current = getModerationCaseOverride(queueCase.caseId);
    patchModerationCaseOverride(queueCase.caseId, {
      auditEvents: [...(current?.auditEvents || []), full],
    });
    const caseRef = queueCase.caseId;
    if (caseRef && full.event) {
      try {
        const stamp = full.dateTime || new Date().toISOString();
        recordCaseActivity({
          caseRef,
          module: 'Moderation',
          sourceModuleRef: 'moderation',
          changeType: full.event,
          activityDetails: full.details || full.event,
          relatedRecord: full.relatedRecord || caseRef,
          changedOnIso: stamp,
          sourceChangeRef: `moderation::${caseRef}::${full.event}::${stamp}`,
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
    }
  };

  const emitContentPublicationFromActions = (actions = [], actor) => {
    const contentType = String(
      queueCase?.contentType || queueCase?.type || detail?.itemType || '',
    );
    const isPost = /post/i.test(contentType);
    const isReview = /review/i.test(contentType);
    if (!isPost && !isReview) return;

    const targetId =
      queueCase?.contentId ||
      queueCase?.postId ||
      queueCase?.reviewId ||
      queueCase?.itemId ||
      detail?.itemId ||
      queueCase?.caseId;
    if (!targetId) return;

    const module = isReview ? 'Reviews' : 'Posts';
    const sourceModuleRef = isReview ? 'reviews_management' : 'posts_management';
    const targetType = isReview ? 'Review' : 'Post';
    const targetName =
      queueCase?.contentTitle ||
      queueCase?.postTitle ||
      queueCase?.reviewTitle ||
      targetId;

    (actions || []).forEach((action) => {
      const type = String(action?.actionType || action?.ruleName || '').toLowerCase();
      let actionLabel = null;
      if (type.includes('hide')) actionLabel = 'Take Down';
      else if (type.includes('remove')) actionLabel = 'Take Down';
      else if (type.includes('restore')) actionLabel = 'Restore';
      else if (type.includes('unpublish')) actionLabel = 'Unpublish';
      else if (type.includes('publish') || type.includes('approve')) actionLabel = 'Publish';
      if (!actionLabel) return;
      try {
        emitPublicationActivity({
          targetType,
          targetId,
          targetName,
          actionLabel,
          outcome: `${action.ruleName || action.actionType || actionLabel} applied via Moderation`,
          actor: actor || 'System',
          module,
          sourceModuleRef,
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
    });
  };

  const handleAssignToMe = () => {
    const caseId = queueCase.caseId;
    if (!caseId || overviewAssignment.caseStatus !== 'New') return;
    const now = new Date().toISOString();
    const actor = 'Alex Chen';
    patchModerationCaseOverride(caseId, {
      status: 'In Progress',
      assignedTo: actor,
      supportTeam: actor,
      lastUpdated: now,
    });
    setOverviewAssignment((prev) => ({
      ...prev,
      assignedHandler: actor,
      caseStatus: 'In Progress',
      lastUpdated: now,
    }));
    appendQueueAudit({
      dateTime: now,
      event: 'Assigned',
      details: `${actor} assigned the case to themselves; New → In Progress`,
      relatedRecord: caseId,
    });
  };

  const handleProgressSave = (draft) => {
    const now = new Date().toISOString();
    const previousStatus = overviewAssignment.caseStatus;
    const nextStatus =
      previousStatus === 'Decided'
        ? 'Decided'
        : previousStatus === 'New'
          ? 'In Progress'
          : normalizeQueueStatus(previousStatus) || 'In Progress';
    patchModerationCaseOverride(queueCase.caseId, {
      decisionDraft: draft,
      supportTeam: overviewAssignment.assignedHandler,
      status: nextStatus,
      lastUpdated: now,
    });
    setOverviewAssignment((prev) => ({ ...prev, caseStatus: nextStatus, lastUpdated: now }));
    if (previousStatus !== nextStatus) {
      appendQueueAudit({
        dateTime: now,
        event: 'Status changed',
        details: `${overviewAssignment.assignedHandler}; ${previousStatus} → ${nextStatus}`,
      });
    }
    appendQueueAudit({
      dateTime: now,
      event: draft?.decisionActionLabel || 'Decision Form Save',
      details: `${overviewAssignment.assignedHandler}; ${
        draft?.decisionOutcome
          ? `${draft.decisionOutcome} recorded`
          : 'decision work saved'
      }`,
    });
  };

  const handleFinalDecisionSave = (record) => {
    const now = record?.finalisedOn || new Date().toISOString();
    const previousStatus = overviewAssignment.caseStatus;
    const actionLabel = record?.decisionActionLabel || record?.decisionActionType || 'Decision Form Save';
    const appliedLabel = record?.appliedActionsLabel || '';
    appendQueueAudit({
      dateTime: now,
      event: actionLabel,
      details: `${record?.finalisedBy || overviewAssignment.assignedHandler}; ${record?.decisionOutcome || 'Decided'}${
        appliedLabel && appliedLabel !== '—' ? `; applied: ${appliedLabel}` : ''
      }`,
      relatedRecord: record?.moderationDecisionId || queueCase.caseId || '—',
    });
    if (record?.decisionOutcome === 'Transfer' || record?.decisionActionType === 'transfer_to_fraud_security') {
      appendQueueAudit({
        dateTime: now,
        event: 'Transfer to Fraud & Security',
        details: `${record?.finalisedBy || overviewAssignment.assignedHandler}; transferred as a new Fraud & Security case (not linked from Moderation)`,
        relatedRecord: queueCase.caseId || '—',
      });
    }
    appendQueueAudit({
      dateTime: now,
      event: 'Status changed',
      details: `${record?.finalisedBy || overviewAssignment.assignedHandler}; ${previousStatus} → Decided`,
    });
    appendQueueAudit({
      dateTime: now,
      event: 'Movement to Archive',
      details: `${record?.finalisedBy || overviewAssignment.assignedHandler}; case moved to Archive`,
    });
    const session = getModerationCaseOverride(queueCase.caseId);
    const queueCaseId = queueCase.caseId;
    const archiveCaseId = archiveIdFromQueueCaseId(queueCaseId);
    const row = {
      ...queueCase,
      caseId: archiveCaseId,
      queueCaseId,
      moderationCaseId: archiveCaseId,
      status: 'Decided',
      supportTeam: overviewAssignment.assignedHandler,
      assignedTo: overviewAssignment.assignedHandler,
      decisionOutcome: record?.decisionOutcome,
      decisionActionType: record?.decisionActionType,
      decisionActionLabel: record?.decisionActionLabel,
      decisionActionRuleId: record?.decisionActionRuleId,
      appliedActions: record?.appliedActions || [],
      appliedActionsLabel: record?.appliedActionsLabel || '',
      moderationDecisionId: record?.moderationDecisionId,
      fraudSecurityCaseId: null,
      fraudSecurityDecisionId: null,
      finalisedOn: now,
      finalisedBy: record?.finalisedBy || overviewAssignment.assignedHandler,
      decisionReason: record?.decisionReason,
      notes: queueNotes,
      auditEvents: session?.auditEvents || [],
    };
    saveDecidedModerationCase(row);
    emitContentPublicationFromActions(
      record?.appliedActions || [],
      record?.finalisedBy || overviewAssignment.assignedHandler,
    );
    setOverviewAssignment((prev) => ({ ...prev, caseStatus: 'Decided', lastUpdated: now }));
    navigate(`/admin/moderation/queue/${archiveCaseId}`, {
      state: { readOnly: true, from: '/admin/moderation/violations', item: row },
    });
  };

  // Click outside handlers for flags row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(flagsRowActionsRefs.current).forEach((flagId) => {
        const ref = flagsRowActionsRefs.current[flagId];
        if (ref && !ref.contains(event.target)) {
          setFlagsRowActionsOpen(prev => ({ ...prev, [flagId]: false }));
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Click outside handler for view flag modal
  useClickOutside(viewFlagModalRef, () => {
    if (viewFlagModalOpen) {
      setViewFlagModalOpen(false);
      setSelectedFlag(null);
    }
  });


  const scenarioAuditLogs = useMemo(() => {
    if (!scenario) return [];
    const c = scenario.case;
    const created = new Date(c.created || Date.now());
    const activeAt = new Date(created.getTime() + 5 * 60 * 1000);
    const decisionAt = new Date(c.updated || created.getTime() + 8 * 60 * 1000);
    const archiveAt = new Date(c.closedDate || decisionAt.getTime() + 60 * 1000);
    const moderatorName = c.assignedTo && c.assignedTo !== 'Unassigned' ? c.assignedTo : 'Sarah K.';
    const logs = [
      {
        id: 's1',
        timestamp: c.created || created.toISOString(),
        performedBy: 'System',
        userType: 'System',
        action: 'Case Created',
        section: 'Moderation Queue',
        summary: 'New moderation case entered the Queue',
      },
      {
        id: 's2',
        timestamp: activeAt.toISOString(),
        performedBy: moderatorName,
        userType: 'Moderation',
        action: 'Status Changed',
        section: 'Moderation Queue',
        summary: 'Case moved from New to In Progress',
      },
      {
        id: 's3',
        timestamp: decisionAt.toISOString(),
        performedBy: moderatorName,
        userType: 'Moderation',
        action: 'Confirm Decision',
        section: 'Decision',
        summary: 'Confirmed Decision recorded via Confirm Decision',
      },
      {
        id: 's4',
        timestamp: archiveAt.toISOString(),
        performedBy: moderatorName,
        userType: 'Moderation',
        action: 'Status Changed',
        section: 'Moderation Queue',
        summary: 'Case moved from In Progress to Decided and handed to Archive',
      },
    ];
    return logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [scenario]);

  const auditLogs = useMemo(() => {
    if (scenario) return [];
    const baseTime = new Date(detail.receivedAt);
    const moderatorName = detail.assignedAdmin || 'Sarah K.';
    const logs = [
      {
        id: 'log-1',
        timestamp: baseTime.toISOString(),
        performedBy: 'System',
        userType: 'System',
        action: 'Case Created',
        section: 'Moderation Queue',
        summary: 'New moderation case entered the Queue',
      },
      {
        id: 'log-2',
        timestamp: new Date(baseTime.getTime() + 53 * 60 * 1000).toISOString(),
        performedBy: moderatorName,
        userType: 'Moderation',
        action: 'Status Changed',
        section: 'Moderation Queue',
        summary: 'Case moved from New to In Progress',
      },
      {
        id: 'log-3',
        timestamp: new Date(baseTime.getTime() + 55 * 60 * 1000).toISOString(),
        performedBy: moderatorName,
        userType: 'Moderation',
        action: 'Decision Form Save',
        section: 'Decision',
        summary: 'Decision Form progress saved',
      },
      {
        id: 'log-4',
        timestamp: new Date(baseTime.getTime() + 57 * 60 * 1000).toISOString(),
        performedBy: moderatorName,
        userType: 'Moderation',
        action: 'Confirm Decision',
        section: 'Decision',
        summary: 'Confirmed outcome recorded via Confirm Decision',
      },
      {
        id: 'log-5',
        timestamp: new Date(baseTime.getTime() + 58 * 60 * 1000).toISOString(),
        performedBy: moderatorName,
        userType: 'Moderation',
        action: 'Internal Note Saved',
        section: 'Internal Notes',
        summary: 'Follow-up note added with supporting context',
      },
      {
        id: 'log-6',
        timestamp: new Date(baseTime.getTime() + 60 * 60 * 1000).toISOString(),
        performedBy: moderatorName,
        userType: 'Moderation',
        action: 'Status Changed',
        section: 'Moderation Queue',
        summary: 'Case moved from In Progress to Decided and handed to Archive',
      },
    ];
    return logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [scenario, detail.receivedAt]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Admin Notes handlers
  const openNotesModal = () => {
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setNoteForm({ noteText: '' });
  };

  const handleNoteSave = () => {
    if (!noteForm.noteText || noteForm.noteText.trim() === '') {
      alert('Note is required');
      return;
    }

    const now = new Date();
    const timestamp = `${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    const date = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    
    // Add new note
    // In real app, get admin name and team from auth context
    const newNote = {
      id: `note-${Date.now()}`,
      timestamp: timestamp,
      createdAt: now.toISOString(),
      date: date,
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      noteContent: noteForm.noteText.trim(),
      content: noteForm.noteText.trim()
    };
    
    setDetail(prev => ({
      ...prev,
      notes: [newNote, ...(prev.notes || [])]
    }));
    
    closeNotesModal();
  };

  const notesEditable =
    !isArchive &&
    overviewAssignment.caseStatus !== 'Decided' &&
    overviewAssignment.caseStatus !== 'New' &&
    overviewAssignment.assignedHandler &&
    overviewAssignment.assignedHandler !== 'Unassigned';

  const noteBody = (note) =>
    [note?.text, ...(note?.additions || []).map((addition) => addition.text)].filter(Boolean).join('\n\n');

  const saveQueueNote = () => {
    if (!notesEditable || !noteEditor) return;
    const text = String(noteEditor.draft || '').trim();
    if (!text) return;
    const now = new Date().toISOString();
    const author =
      overviewAssignment.assignedHandler !== 'Unassigned'
        ? overviewAssignment.assignedHandler
        : 'Alex Chen';
    let nextNotes = queueNotes;
    if (noteEditor.mode === 'add') {
      nextNotes = [
        ...queueNotes,
        {
          id: `NOTE-${Date.now()}`,
          text,
          addedBy: author,
          addedOn: now,
          additions: [],
        },
      ];
      appendQueueAudit({
        dateTime: now,
        event: 'Internal Note Saved',
        details: `${author} saved an internal note.`,
      });
    } else if (noteEditor.mode === 'edit' && noteEditor.noteId) {
      nextNotes = queueNotes.map((note) =>
        note.id === noteEditor.noteId
          ? {
              ...note,
              additions: [...(note.additions || []), { text, addedBy: author, addedOn: now }],
            }
          : note
      );
      appendQueueAudit({
        dateTime: now,
        event: 'Internal Note Saved',
        details: `${author} added text to an Internal Note`,
      });
    }
    setQueueNotes(nextNotes);
    patchModerationCaseOverride(queueCase.caseId, {
      notes: nextNotes,
      supportTeam: overviewAssignment.assignedHandler,
      status: overviewAssignment.caseStatus,
      lastUpdated: now,
    });
    setOverviewAssignment((prev) => ({ ...prev, lastUpdated: now }));
    setNoteEditor(null);
  };

  const buildLinkedAppealCaseId = (originalCaseId) => {
    if (!originalCaseId) return `MOD-${Date.now()}`;
    const match = String(originalCaseId).match(/(\d+)/);
    const base = match ? Number(match[1]) : Date.now();
    return `MOD-${String(base + 2000).padStart(6, '0')}`;
  };

  const handleCreateAppealCase = (caseData) => {
    if (!appealForm.requestedBy) {
      alert('Please select Requested By');
      return;
    }
    if (!appealForm.appealSummary.trim()) {
      alert('Please provide Appeal Summary');
      return;
    }

    const nowIso = new Date().toISOString();
    const linkedAppealCaseId = buildLinkedAppealCaseId(caseData.caseId || caseId);
    const performedBy = caseData.closedBy || 'Moderation — ABC Name';
    const nextAuditLog = Array.isArray(caseData.auditLog) ? [...caseData.auditLog] : [];
    nextAuditLog.push({
      dateTime: nowIso,
      performedBy,
      userType: 'Moderation',
      action: 'Appeal Created',
      section: 'Appeal',
      summary: `Linked appeal case ${linkedAppealCaseId} created.`,
    });

    setArchiveCaseState((prev) => ({
      ...prev,
      appealStatus: 'Appeal Open',
      linkedAppealCaseId,
      appealDetails: {
        requestedBy: appealForm.requestedBy,
        appealSummary: appealForm.appealSummary.trim(),
        createdAt: nowIso,
      },
      auditLog: nextAuditLog,
    }));

    setAppealForm({
      requestedBy: '',
      appealSummary: '',
    });
    alert(`Appeal case created: ${linkedAppealCaseId}. New case should be sent to Moderation Queue.`);
  };

  const getRefLink = (linkedRef) => {
    if (!linkedRef || !linkedRef.id) return null;
    switch (linkedRef.type) {
      case 'post':
        return `/admin/posts/${linkedRef.id}`;
      case 'business':
        return `/admin/users/businesses/${linkedRef.id}`;
      case 'moderation':
        return `/admin/moderation/queue/${linkedRef.id}`;
      default:
        return null;
    }
  };

  const getRefDisplay = (linkedRef) => {
    if (!linkedRef || !linkedRef.id) return '—';
    return `${linkedRef.type.toUpperCase()}#${linkedRef.id}`;
  };

  const timeInQueue = useMemo(() => {
    const diff = now.getTime() - new Date(detail.receivedAt).getTime();
    return formatDuration(diff);
  }, [now, detail.receivedAt]);

  const renderOverview = () => {
    if (isArchive) {
      const model = buildArchiveOverviewModel({
        scenario,
        itemFromState: archiveCase,
        formatDateTime: formatOverviewDateTime,
      });
      return (
        <ModerationOverviewTab
          variant="archive"
          model={model}
          assignment={{
            assignedHandler:
              archiveCase.finalisedBy && archiveCase.finalisedBy !== '—'
                ? archiveCase.finalisedBy
                : archiveCase.supportTeam || overviewAssignment.assignedHandler,
            caseStatus: 'Decided',
          }}
        />
      );
    }

    const model = buildModerationOverviewModel({
      scenario,
      itemFromState: {
        ...queueCase,
        status: overviewAssignment.caseStatus,
        supportTeam: overviewAssignment.assignedHandler,
        assignedTo: overviewAssignment.assignedHandler,
        updated: overviewAssignment.lastUpdated || queueCase.updated,
      },
      formatDateTime: formatOverviewDateTime,
    });

    return (
      <ModerationOverviewTab
        model={model}
        assignment={overviewAssignment}
      />
    );
  };

  const renderCapturedEvidenceHeader = () => {
    const sourceRow = isArchive ? archiveCase : queueCase;
    const evidence = buildCapturedEvidence(
      {
        ...sourceRow,
        type: sourceRow.queueType || sourceRow.type,
      },
      scenario
    );
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-4 mb-8">
        <div className="space-y-1">
          <AdminDetailFieldLabel>Type</AdminDetailFieldLabel>
          <AdminDetailFieldValue>{evidence.type}</AdminDetailFieldValue>
        </div>
        <div className="space-y-1">
          <AdminDetailFieldLabel>Source Module</AdminDetailFieldLabel>
          <AdminDetailFieldValue>{evidence.sourceModule}</AdminDetailFieldValue>
        </div>
        <div className="space-y-1">
          <AdminDetailFieldLabel>Source Record ID</AdminDetailFieldLabel>
          <AdminDetailFieldValue>{evidence.sourceRecordId}</AdminDetailFieldValue>
        </div>
        <div className="space-y-1">
          <AdminDetailFieldLabel>Captured Snapshot Reference</AdminDetailFieldLabel>
          <AdminDetailFieldValue>{evidence.snapshotRef}</AdminDetailFieldValue>
        </div>
        <div className="space-y-1">
          <AdminDetailFieldLabel>Captured At</AdminDetailFieldLabel>
          <AdminDetailFieldValue>{evidence.capturedAt}</AdminDetailFieldValue>
        </div>
        <div className="space-y-1">
          <AdminDetailFieldLabel>Exact Flagged Item Reference(s)</AdminDetailFieldLabel>
          <AdminDetailFieldValue>{evidence.flaggedItemRefs}</AdminDetailFieldValue>
        </div>
        <div className="space-y-1">
          <AdminDetailFieldLabel>Content Format</AdminDetailFieldLabel>
          <AdminDetailFieldValue>{evidence.contentFormat}</AdminDetailFieldValue>
        </div>
        {buildSourceStateFields(sourceRow, scenario, { includeDecision: isArchive }).map((field) => (
          <div key={field.label} className="space-y-1">
            <AdminDetailFieldLabel>{field.label}</AdminDetailFieldLabel>
            <AdminDetailFieldValue>{field.value}</AdminDetailFieldValue>
          </div>
        ))}
      </div>
    );
  };

  const renderContentAndEvidence = () => {
    const caseData = itemFromState || detail;
    const { content } = detail;
    const itemType = caseData.type || detail.itemType;

    if (scenario && isProfileScenario) {
      const s = scenario;
      const pc = s.profileContent || {};
      const profileName = s.profileType === 'Customer' ? (s.customer?.name || pc.displayName || '—') : (s.business?.businessName || '—');
      const profileImageUrl = pc.profileImageUrl || (s.profileType === 'Customer' ? s.customer?.avatar : s.business?.logo) || null;
      const reportScreenshots = s.flag?.reportScreenshots || [];
      return (
        <>
          <div className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-primaryblue/10 border border-lightgray flex-shrink-0">
                  <img
                    src={profileImageUrl || Profile}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = Profile; }}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <AdminMainText className="text-offblack font-semibold">{profileName}</AdminMainText>
                  {(s.profileType === 'Customer' && s.customer?.customerId) || (s.profileType === 'Business' && s.business?.businessId) ? (
                    <AdminHeaderAction
                    onClick={() =>{
                        const id = s.profileType === 'Customer' ? s.customer.customerId : s.business.businessId;
                        window.open(`/poster-profile/?id=${encodeURIComponent(id)}`, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      View public profile</AdminHeaderAction>
                  ) : null}
                </div>
              </div>
              {reportScreenshots.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                  {reportScreenshots.slice(0, 2).map((url, index) => (
                    <button
                      key={`${url}-${index}`}
                      type="button"
                      className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue"
                      onClick={() => setPreviewMedia({ url })}
                    >
                      <img
                        src={url}
                        alt=""
                        className="h-40 w-full object-cover transition-transform duration-200 hover:scale-105"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/300?text=Image'; }}
                      />
                    </button>
                  ))}
                </div>
              )}
          </div>
          {previewMedia && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
              onClick={() => setPreviewMedia(null)}
            >
              <div className="relative max-w-6xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="absolute -top-10 right-0 text-white hover:text-primaryblue text-sm font-medium" onClick={() => setPreviewMedia(null)}>Close</button>
                <div className="relative rounded-lg overflow-hidden bg-white">
                  <img src={previewMedia.url} alt="Full view" className="w-full h-full object-contain max-h-[90vh]" onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/800?text=Image'; }} />
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    if (scenario && isReviewScenario) {
      const r = scenario.review;
      const formatReviewDate = (v) => {
        if (!v) return '—';
        return new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      };
      return (
        <>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-primaryblue/10 border border-lightgray flex-shrink-0">
                      <img
                        src={r.reviewer?.avatar || Profile}
                        alt={r.reviewer?.name || 'Customer'}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.src = Profile; }}
                      />
                    </div>
                    <div>
                      <div className="text-sm text-offblack font-semibold">{r.reviewer?.name || '—'}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-offblack">
                    <div className="flex items-center gap-2">
                      <Rating value={r.rating} cancel={false} readOnly style={{ gap: 4 }} />
                      <span className="text-xs font-semibold whitespace-nowrap">
                        ({Number.isFinite(r.rating) ? r.rating.toFixed(1) : r.rating ?? '—'})
                      </span>
                    </div>
                    <span className="hidden sm:inline text-mediumgray">•</span>
                    <span className="whitespace-nowrap">Posted: {formatReviewDate(r.postedAt)}</span>
                  </div>
                  <AdminMainText className="text-offblack max-w-3xl whitespace-pre-wrap">
                    {r.fullText || r.title || '—'}
                  </AdminMainText>
                  {(() => {
                    const reviewImages = (r.images || r.attachments || []).map((item) =>
                      typeof item === 'string' ? item : (item?.url || item?.src)
                    ).filter(Boolean);
                    return reviewImages.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
                        {reviewImages.map((imageUrl, index) => (
                          <button
                            key={`${imageUrl}-${index}`}
                            type="button"
                            className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue"
                            onClick={() => setPreviewMedia({ url: imageUrl })}
                          >
                            <img
                              src={imageUrl}
                              alt=""
                              className="h-32 w-full object-cover transition-transform duration-200 hover:scale-105"
                              onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/300?text=Image'; }}
                            />
                          </button>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>
          {previewMedia && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
              onClick={() => setPreviewMedia(null)}
            >
              <div className="relative max-w-6xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="absolute -top-10 right-0 text-white hover:text-primaryblue text-sm font-medium" onClick={() => setPreviewMedia(null)}>Close</button>
                <div className="relative rounded-lg overflow-hidden bg-white">
                  <img src={previewMedia.url} alt="Full view" className="w-full h-full object-contain max-h-[90vh]" onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/800?text=Image'; }} />
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    if (scenario && isMessageScenario) {
      const s = scenario;
      const messages = s.messages || [];
      const flaggedIndex = messages.findIndex((m) => m.isFlagged);
      const idx = flaggedIndex >= 0 ? flaggedIndex : 0;
      const sliceStart = Math.max(0, idx - 1);
      const sliceEnd = Math.min(messages.length, idx + 2);
      const showMessages = messages.slice(sliceStart, sliceEnd);
      const formatMsgTime = (ts) => {
        if (!ts) return '—';
        const d = new Date(ts);
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      };
      const customerAvatar = s.customer?.avatar || Profile;
      const businessAvatar = s.business?.avatar || BusinessFallback;
      return (
        <>
              <div className="flex flex-col space-y-4" style={{ minHeight: '300px' }}>
                {showMessages.length === 0 ? (
                  <AdminSupportingText>
                    No messages in this conversation.
                  </AdminSupportingText>
                ) : (
                  showMessages.map((msg) => {
                    const isBusiness = msg.sender === 'Business';
                    const senderKey = isBusiness ? 'business' : 'customer';
                    const avatar = isBusiness ? businessAvatar : customerAvatar;
                    const name = isBusiness ? (s.business?.businessName || 'Business') : (s.customer?.name || 'Customer');
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isBusiness ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`flex max-w-[85%] ${isBusiness ? 'flex-row-reverse' : 'flex-row'} items-end gap-2`}>
                          <div className="flex-shrink-0">
                            <img
                              src={avatar}
                              alt={name}
                              className="w-8 h-8 rounded-full object-cover"
                              onError={(e) => { e.target.src = Profile; }}
                            />
                          </div>
                          <div className="flex flex-col">
                            <div
                              className={`px-4 py-2 rounded-2xl ${
                                isBusiness ? 'bg-primaryblue text-white' : 'bg-mistygray text-offblack'
                              } ${isBusiness ? 'rounded-br-md' : 'rounded-bl-md'} border ${msg.isFlagged ? 'border-alertred' : 'border-transparent'}`}
                            >
                              <AdminMainText className={isBusiness ? 'text-white leading-relaxed' : 'text-offblack leading-relaxed'}>
                                {msg.text}
                              </AdminMainText>
                            </div>
                            <div className={`mt-1 px-2 ${isBusiness ? 'text-right' : 'text-left'}`}>
                              <AdminMetadataText className="text-xs text-mediumgray">
                                {formatMsgTime(msg.timestamp)}
                              </AdminMetadataText>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
        </>
      );
    }

    if (scenario && isQuoteScenario) {
      const s = scenario;
      const chatMessages = s.chatMessages || [];
      const formatMsgTime = (ts) => {
        if (!ts) return '—';
        const d = new Date(ts);
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      };
      const customerAvatar = s.customer?.avatar || Profile;
      const businessAvatar = s.business?.avatar || BusinessFallback;
      return (
        <>
              <div className="flex flex-col space-y-4" style={{ minHeight: '300px' }}>
                {chatMessages.length === 0 ? (
                  <AdminSupportingText>
                    No chat messages in this quote thread.
                  </AdminSupportingText>
                ) : (
                  chatMessages.map((msg) => {
                    const isBusiness = msg.sender === 'Business';
                    const avatar = isBusiness ? businessAvatar : customerAvatar;
                    const name = isBusiness ? (s.business?.businessName || 'Business') : (s.customer?.name || 'Customer');
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isBusiness ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`flex max-w-[85%] ${isBusiness ? 'flex-row-reverse' : 'flex-row'} items-end gap-2`}>
                          <div className="flex-shrink-0">
                            <img
                              src={avatar}
                              alt={name}
                              className="w-8 h-8 rounded-full object-cover"
                              onError={(e) => { e.target.src = Profile; }}
                            />
                          </div>
                          <div className="flex flex-col">
                            <div
                              className={`px-4 py-2 rounded-2xl ${
                                isBusiness ? 'bg-primaryblue text-white' : 'bg-mistygray text-offblack'
                              } ${isBusiness ? 'rounded-br-md' : 'rounded-bl-md'} border ${msg.isFlagged ? 'border-alertred' : 'border-transparent'}`}
                            >
                              <AdminMainText className={isBusiness ? 'text-white leading-relaxed' : 'text-offblack leading-relaxed'}>
                                {msg.text}
                              </AdminMainText>
                            </div>
                            <div className={`mt-1 px-2 ${isBusiness ? 'text-right' : 'text-left'}`}>
                              <AdminMetadataText className="text-xs text-mediumgray">
                                {formatMsgTime(msg.timestamp)}
                              </AdminMetadataText>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
        </>
      );
    }

    if (scenario?.post) {
      const p = scenario.post;
      const gallery = (detail.content?.gallery && detail.content.gallery.length > 0)
        ? detail.content.gallery
        : (p.images || []).map((img) => ({ url: img.url, flagged: false }));
      return (
        <>
          <div className="space-y-8">
                <div className="space-y-3">
                  <AdminDetailFieldLabel>Post Description</AdminDetailFieldLabel>
                  <AdminMainText className="text-offblack whitespace-pre-line">
                    {p.description || '—'}
                  </AdminMainText>
                </div>
                <div className="space-y-4">
                  <AdminDetailFieldLabel>Attached Media</AdminDetailFieldLabel>
                  {gallery.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {gallery.map((item, index) => (
                        <button
                          key={item.url || index}
                          type="button"
                          onClick={() => setPreviewMedia(item)}
                          className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue cursor-pointer hover:opacity-90 transition-opacity"
                        >
                          <img
                            src={item.url}
                            alt={`Post image ${index + 1}`}
                            className="h-32 w-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/300?text=Image';
                            }}
                          />
                          {item.flagged && (
                            <span className="absolute top-2 right-2 px-2 py-1 rounded-full bg-alertred text-white text-[10px] uppercase tracking-wide">
                              Flagged
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <AdminSupportingText>
                      No images available
                    </AdminSupportingText>
                  )}
                </div>
          </div>
          {previewMedia && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
              onClick={() => setPreviewMedia(null)}
            >
              <div className="relative max-w-6xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="absolute -top-10 right-0 text-white hover:text-primaryblue text-sm font-medium transition-colors"
                  onClick={() => setPreviewMedia(null)}
                >
                  Close
                </button>
                <div className="relative rounded-lg overflow-hidden bg-white">
                  <img
                    src={previewMedia.url}
                    alt="Full image view"
                    className="w-full h-full object-contain max-h-[90vh]"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/800?text=Image+Not+Available';
                    }}
                  />
                  {previewMedia.flagged && (
                    <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-alertred text-white text-xs uppercase tracking-wide">
                      Flagged
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    if (!content) {
      return (
        <AdminSupportingText className="text-mediumgray">
          No content available
        </AdminSupportingText>
      );
    }

    return (
      <>
        <div className="space-y-8">
            {itemType === 'Post' && content.postText && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Title</AdminPanelTitle>
                <AdminMainText className="text-offblack font-semibold">
                  {itemTitle}
                </AdminMainText>
                <div className="space-y-3 pt-4">
                  <AdminDetailFieldLabel>Post Description</AdminDetailFieldLabel>
                  <AdminMainText className="text-offblack whitespace-pre-line">
                    {content.postText}
                  </AdminMainText>
                </div>
                {content.gallery && content.gallery.length > 0 && (
                  <div className="space-y-4 pt-4">
                    <AdminDetailFieldLabel>Attached Media</AdminDetailFieldLabel>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {content.gallery.map((item, index) => (
                        <button
                          key={item.url || index}
                          type="button"
                          onClick={() => setPreviewMedia(item)}
                          className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue cursor-pointer hover:opacity-90 transition-opacity"
                        >
                          <img 
                            src={item.url} 
                            alt={`Media ${index + 1}`} 
                            className="h-32 w-full object-cover" 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/300?text=Image';
                            }}
                          />
                          {item.flagged && (
                            <span className="absolute top-2 right-2 px-2 py-1 rounded-full bg-alertred text-white text-[10px] uppercase tracking-wide">
                              Flagged
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {itemType === 'Review' && content.reviewText && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <AdminPanelTitle className="text-offblack">Rating</AdminPanelTitle>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <i 
                        key={i}
                        className={`fi ${i < 4 ? 'fi-sr-star' : 'fi-rr-star'} text-xs ${
                          i < 4 ? 'text-warmyellow' : 'text-lightgray'
                        }`}
                      ></i>
                    ))}
                  </div>
                </div>
                <AdminPanelTitle className="text-offblack">Review text</AdminPanelTitle>
                <AdminMainText className="text-offblack whitespace-pre-line">
                  {content.reviewText}
                </AdminMainText>
                {content.gallery && content.gallery.length > 0 && (
                  <>
                    <AdminPanelTitle className="text-offblack mt-4">Images</AdminPanelTitle>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {content.gallery.map((item, index) => (
                        <button
                          key={item.url || index}
                          type="button"
                          onClick={() => setPreviewMedia(item)}
                          className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue cursor-pointer hover:opacity-90 transition-opacity"
                        >
                          <img 
                            src={item.url} 
                            alt={`Review image ${index + 1}`} 
                            className="h-32 w-full object-cover" 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/300?text=Image';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {itemType === 'Message' && content.messageThread && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Flagged message highlighted</AdminPanelTitle>
                <div className="space-y-4">
                  {content.messageThread.map((msg, idx) => {
                    const isFlagged = idx === 1; // Highlight the second message as flagged
                    return (
                      <div key={idx} className={`border rounded-lg p-4 ${isFlagged ? 'border-alertred bg-red-50' : 'border-lightgray'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <AdminMainText className="text-offblack font-semibold">
                          {msg.sender}
                        </AdminMainText>
                        <AdminMetadataText>
                          {msg.timestamp}
                        </AdminMetadataText>
                      </div>
                      <AdminMainText className="text-offblack">
                        {msg.text}
                      </AdminMainText>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {itemType === 'Business Profile' && content.businessFields && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Flagged text</AdminPanelTitle>
                <AdminMainText className="text-offblack bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  {content.businessFields.description || '—'}
                </AdminMainText>
                <AdminPanelTitle className="text-offblack mt-4">Description fields</AdminPanelTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(content.businessFields).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <AdminDetailFieldLabel>
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                        {value || '—'}
                      </AdminDetailFieldValue>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {itemType === 'Customer Profile' && content.customerFields && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Flagged bio section</AdminPanelTitle>
                <AdminMainText className="text-offblack bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  {content.customerFields.bio || 'No bio available'}
                </AdminMainText>
                <AdminPanelTitle className="text-offblack mt-4">Profile fields</AdminPanelTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(content.customerFields).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <AdminDetailFieldLabel>
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                        {value || '—'}
                      </AdminDetailFieldValue>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {itemType === 'Document' && content.documents && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Preview</AdminPanelTitle>
                {content.gallery && content.gallery.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {content.gallery.map((item, index) => (
                      <button
                        key={item.url || index}
                        type="button"
                        onClick={() => setPreviewMedia(item)}
                        className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        <img 
                          src={item.url} 
                          alt={`Document preview ${index + 1}`} 
                          className="h-48 w-full object-cover" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/300?text=Document';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  <AdminSupportingText>
                    No preview available
                  </AdminSupportingText>
                )}
                <AdminPanelTitle className="text-offblack mt-4">Document Information</AdminPanelTitle>
                <div className="space-y-3">
                  {content.documents.map((doc, idx) => (
                    <div key={idx} className="border border-lightgray rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Issue/Expiry</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{doc.uploadedDate || '—'}</AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Issuer</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{doc.type || '—'}</AdminDetailFieldValue>
                          </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(itemType === 'Direct Quote' || itemType === 'Group Quote') && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Quote Content</AdminPanelTitle>
                <AdminMainText className="text-offblack whitespace-pre-line">
                  {itemTitle}
                </AdminMainText>
              </div>
            )}

            {/* Media Section */}
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack">Media</AdminPanelTitle>
              {content.gallery && content.gallery.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {content.gallery.map((item, index) => (
                    <button
                      key={item.url || index}
                      type="button"
                      onClick={() => setPreviewMedia(item)}
                      className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue cursor-pointer hover:opacity-90 transition-opacity"
                    >
                      <img 
                        src={item.url} 
                        alt={`Evidence image ${index + 1}`} 
                        className="h-32 w-full object-cover" 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/300?text=Image';
                        }}
                      />
                      {item.flagged && (
                        <span className="absolute top-2 right-2 px-2 py-1 rounded-full bg-alertred text-white text-[10px] uppercase tracking-wide">
                          Flagged
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <AdminSupportingText>
                  No images available
                </AdminSupportingText>
              )}

              {/* Screenshots Section */}
              {content.attachments && content.attachments.filter(att => att.type === 'screenshot').length > 0 && (
                <div className="space-y-3 mt-6">
                  <AdminPanelTitle className="text-offblack">Screenshots</AdminPanelTitle>
                  <div className="space-y-2">
                    {content.attachments
                      .filter(att => att.type === 'screenshot')
                      .map((file, idx) => (
                        <div key={idx} className="border border-lightgray rounded-lg p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-lightgray rounded flex items-center justify-center">
                              <span className="text-base" aria-hidden>📷</span>
                            </div>
                            <div>
                              <div className="text-sm text-offblack font-medium">
                                {file.name}
                              </div>
                              <AdminMetadataText>
                                {file.size || 'Unknown size'}
                              </AdminMetadataText>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="text-offblack text-sm font-medium cursor-pointer"
                            onClick={() => window.open(file.url, '_blank')}
                          >
                            View
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
        </div>

        {/* Full Image Viewer Modal */}
        {previewMedia && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
            onClick={() => setPreviewMedia(null)}
          >
            <div
              className="relative max-w-6xl w-full max-h-[90vh]"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="absolute -top-10 right-0 text-white hover:text-primaryblue text-sm font-medium transition-colors"
                onClick={() => setPreviewMedia(null)}
              >
                Close
              </button>
              <div className="relative rounded-lg overflow-hidden bg-white">
                <img 
                  src={previewMedia.url} 
                  alt="Full image view" 
                  className="w-full h-full object-contain max-h-[90vh]" 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/800?text=Image+Not+Available';
                  }}
                />
                {previewMedia.flagged && (
                  <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-alertred text-white text-xs uppercase tracking-wide">
                    Flagged
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const toggleFlagsRowActions = (flagId) => {
    setFlagsRowActionsOpen((prev) => {
      if (prev[flagId]) return {};
      return { [flagId]: true };
    });
  };

  const renderFlags = () => {
    const caseData = itemFromState || detail;

    let flagsData = [];

    if (scenario) {
      const f = scenario.flag;
      const flagStatus = f.status === 'Closed' ? 'Reviewed' : f.status === 'In Progress' ? 'New' : (f.status || 'New');
      const target = scenario.profileType === 'Customer' ? 'Customer' : scenario.profileType === 'Business' ? 'Business' : scenario.review ? 'Review' : scenario.conversation ? 'Message' : scenario.quote ? 'Quote' : 'Post';
      flagsData = [
        {
          flagId: f.flagId,
          flagType: f.source,
          flagStatus,
          reason: f.trigger,
          target,
          created: formatDateTime(f.createdAt || scenario.case.created),
          lastUpdated: formatDateTime(scenario.case.closedDate || scenario.case.updated || f.createdAt),
          reporterNote: f.source === 'Customer' ? 'User reported this content' : null,
        },
      ];
    } else {
      const flagType = caseData.flag || (caseData.source === 'User-Reported' ? 'Customer' : 
                                        caseData.source === 'Admin-Escalated' ? 'Admin' : 
                                        caseData.source === 'System-Flagged' ? 'System' : 'Customer');
      const reason = caseData.reason || mapViolationToReason(caseData.violation || caseData.originalViolation);
      const contentType = caseData.contentType || normalizeContentType(caseData.type || detail.itemType);

      flagsData.push({
        flagId: `FLG-${String(145 + Math.floor(Math.random() * 100)).padStart(6, '0')}`,
        flagType: flagType,
        flagStatus: 'New',
        reason: reason,
        target: contentType === 'Quote' ? 'Quote' : 
                contentType === 'Review' ? 'Review' : 
                contentType === 'Message' ? 'Message' : 
                contentType === 'Document' ? 'Document' : 
                contentType === 'Post' ? 'Post' : 'Post',
        created: formatDateTime(caseData.created || detail.receivedAt || new Date()),
        lastUpdated: formatDateTime(caseData.updated || detail.lastUpdatedAt || new Date()),
        reporterNote: caseData.source === 'User-Reported' ? 'User reported this content' : null,
      });

      if (caseData.source === 'System-Flagged') {
        flagsData.push({
          flagId: `FLG-${String(139 + Math.floor(Math.random() * 100)).padStart(6, '0')}`,
          flagType: 'System',
          flagStatus: 'Reviewed',
          reason: 'Scam',
          target: contentType,
          created: formatDateTime(new Date(Date.now() - 2 * 60 * 60 * 1000)),
          lastUpdated: formatDateTime(new Date(Date.now() - 1 * 60 * 60 * 1000)),
          reporterNote: null,
        });
      }

      if (caseData.source === 'User-Reported') {
        flagsData.push({
          flagId: `FLG-${String(131 + Math.floor(Math.random() * 100)).padStart(6, '0')}`,
          flagType: 'Customer',
          flagStatus: 'Dismissed',
          reason: 'Policy',
          target: contentType,
          created: formatDateTime(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)),
          lastUpdated: formatDateTime(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000 + 8 * 60 * 1000)),
          reporterNote: 'User used abusive language',
        });
      }
    }

    // Helper function to map violation to reason
    function mapViolationToReason(violation) {
      if (!violation) return 'Other';
      const upperViolation = violation.toUpperCase();
      if (upperViolation.includes('HARASSMENT')) return 'Harassment';
      if (upperViolation.includes('SCAM')) return 'Scam';
      if (upperViolation.includes('ABUSE')) return 'Abuse';
      if (upperViolation.includes('SPAM')) return 'Spam';
      if (upperViolation.includes('COMPLIANCE')) return 'Compliance';
      if (upperViolation.includes('COPYRIGHT')) return 'Other'; // Copyright not in flag reasons
      if (upperViolation.includes('POLICY') || upperViolation.includes('FAKE') || upperViolation.includes('INAPPROPRIATE')) return 'Policy';
      return 'Other';
    }

    // Helper function to normalize content type
    function normalizeContentType(type) {
      if (!type) return 'Post';
      if (type === 'Direct Quote' || type === 'Group Quote') return 'Quote';
      if (type === 'Business Profile' || type === 'Customer Profile') return 'Profile';
      if (['Message', 'Review', 'Quote', 'Post', 'Document', 'Profile'].includes(type)) return type;
      return 'Post';
    }

    const handleViewFlag = (flag) => {
      setSelectedFlag(flag);
      setViewFlagModalOpen(true);
      setFlagsRowActionsOpen({});
    };

    const handleOpenContent = () => {
      setActiveTab('content');
      setFlagsRowActionsOpen({});
    };

    const moderationFlagsGrid = isArchive
      ? 'minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 1.1fr) minmax(0, 1.1fr)'
      : 'minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(56px, auto)';
    const flagsPagination = getDetailTablePagination(flagsData.length, flagsCurrentPage);
    const paginatedFlagsData = flagsPagination.paginate(flagsData);

    return (
      <div className="space-y-6 min-w-0 max-w-full">
      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
        {/* Header */}
          <div className="grid min-w-0 py-3 text-offblack border-b border-lightgray px-4 gap-x-2 sm:gap-x-3 gap-y-2" style={{ 
            gridTemplateColumns: moderationFlagsGrid,
          }}>
          <div className="flex items-center justify-start text-left">
              <AdminTableHeader>Flag ID</AdminTableHeader>
          </div>
          <div className="flex items-center justify-start text-left">
              <AdminTableHeader>{isArchive ? 'Flag Source' : 'Flag Type'}</AdminTableHeader>
          </div>
          <div className="flex items-center justify-start text-left">
              <AdminTableHeader>Flag Status</AdminTableHeader>
          </div>
          <div className="flex items-center justify-start text-left">
              <AdminTableHeader>Reason</AdminTableHeader>
          </div>
          <div className="flex items-center justify-start text-left">
              <AdminTableHeader>Target</AdminTableHeader>
          </div>
            {isArchive ? (
              <>
          <div className="flex items-center justify-start text-left">
                  <AdminTableHeader>Reporter Note</AdminTableHeader>
          </div>
          <div className="flex items-center justify-start text-left">
                  <AdminTableHeader>Created Date</AdminTableHeader>
          </div>
                <div className="flex items-center justify-start text-left">
                  <AdminTableHeader>Reviewed Date</AdminTableHeader>
                </div>
              </>
            ) : (
              <>
          <div className="flex items-center justify-start text-left">
            <AdminTableHeader>Last Updated</AdminTableHeader>
          </div>
                <div className="flex items-center justify-start text-left">
                  <AdminTableHeader>Created</AdminTableHeader>
          </div>
          <div className="flex items-center justify-start text-left">
            <AdminTableHeader>Actions</AdminTableHeader>
          </div>
              </>
            )}
        </div>

        {/* Rows */}
          {flagsData.length === 0 ? (
            <div className="py-10 text-center">
              <AdminSupportingText>
                No flags found for this case.
              </AdminSupportingText>
            </div>
          ) : (
            paginatedFlagsData.map((flag) => (
              <div
                key={flag.flagId}
            className="grid min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                style={{ gridTemplateColumns: moderationFlagsGrid }}
          >
            <div className="flex items-center justify-start text-left min-w-0">
                  <AdminTableBody className="min-w-0 break-words leading-snug">{flag.flagId}</AdminTableBody>
            </div>
            <div className="flex items-center justify-start text-left min-w-0">
                  <AdminTableBody className="min-w-0 break-words leading-snug">{flag.flagType}</AdminTableBody>
            </div>
            <div className="flex items-center justify-start text-left">
                  <AdminStatusBadge status={flag.flagStatus} label={flag.flagStatus} />
            </div>
            <div className="flex items-center justify-start text-left">
                  <span >{flag.reason}</span>
            </div>
                <div className="flex items-center justify-start text-left">
                  <span  title={flag.target}>{flag.target}</span>
            </div>
                {isArchive ? (
                  <>
                    <div className="flex items-center justify-start text-left">
                      <span className="text-xs" title={flag.reporterNote || ''}>
                        {flag.reporterNote || '—'}
                      </span>
            </div>
            <div className="flex items-center justify-start text-left">
                      <span className="text-xs">{flag.created}</span>
            </div>
                    <div className="flex items-center justify-start text-left">
                      <span className="text-xs">
                        {flag.flagStatus === 'New' ? '—' : (flag.lastUpdated || '—')}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
            <div className="flex items-center justify-start text-left">
              <span className="text-xs">{flag.lastUpdated}</span>
            </div>
                    <div className="flex items-center justify-start text-left">
                      <span className="text-xs">{flag.created}</span>
            </div>
            <div className="flex items-center relative" onClick={(e) => e.stopPropagation()}>
                      <div className="relative" ref={el => flagsRowActionsRefs.current[flag.flagId] = el}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                            toggleFlagsRowActions(flag.flagId);
                  }}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                  title="Row Actions"
                >
                  <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                </button>
                        {flagsRowActionsOpen[flag.flagId] && (
                  <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                      <AdminRowMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewFlag(flag);
                        }}
                      >
                        View Flag
                      </AdminRowMenuItem>
                      <AdminRowMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenContent();
                        }}
                      >
                        Open Content
                      </AdminRowMenuItem>
                  </div>
                )}
              </div>
            </div>
                  </>
                )}
          </div>
            ))
          )}
      </DashboardMainContainer>

        {/* View Flag Modal */}
        {viewFlagModalOpen && selectedFlag && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div ref={viewFlagModalRef} className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <AdminPanelTitle className="text-offblack">Flag Details</AdminPanelTitle>
                  <button
                    onClick={() => {
                      setViewFlagModalOpen(false);
                      setSelectedFlag(null);
                    }}
                    className="text-offblack hover:text-primaryblue"
                  >
                    <i className="fi fi-rs-cross dashboard-icon"></i>
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <AdminDetailFieldLabel>Flag ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{selectedFlag.flagId}</AdminDetailFieldValue>
                  </div>
                  
                  <div>
                    <AdminDetailFieldLabel>Flag Type</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{selectedFlag.flagType}</AdminDetailFieldValue>
                  </div>
                  
                  <div>
                    <AdminDetailFieldLabel>Reason</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{selectedFlag.reason}</AdminDetailFieldValue>
                  </div>
                  
                  <div>
                    <AdminDetailFieldLabel>Target</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{selectedFlag.target}</AdminDetailFieldValue>
                  </div>
                  
                  {selectedFlag.reporterNote && (
                    <div>
                      <AdminDetailFieldLabel>Reporter Note</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>{selectedFlag.reporterNote}</AdminDetailFieldValue>
                    </div>
                  )}
                  
                  <div>
                    <AdminDetailFieldLabel>Created</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{selectedFlag.created}</AdminDetailFieldValue>
                  </div>
                  
                  <div>
                    <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{selectedFlag.flagStatus}</AdminDetailFieldValue>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-lightgray">
                  <AdminRowAction
                    onClick={() =>{
                      setViewFlagModalOpen(false);
                      setSelectedFlag(null);
                    }}
                  >
                    Close
                  </AdminRowAction>
                </div>
              </div>
            </div>
          </div>
        )}
      <ConfigurationListPaginationFooter
        show={flagsPagination.showPagination}
        rangeStart={flagsPagination.rangeStart}
        rangeEnd={flagsPagination.rangeEnd}
        totalCount={flagsData.length}
        currentPage={flagsPagination.effectivePage}
        totalPages={flagsPagination.totalPages}
        onPrev={() => setFlagsCurrentPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setFlagsCurrentPage((prev) => Math.min(flagsPagination.totalPages, prev + 1))}
        goToPage={setFlagsCurrentPage}
      />
      </div>
    );
  };

  const renderFlagInformation = () => {
    const { flagInfo } = detail;
    if (!flagInfo) {
      return (
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <AdminMainText className="text-offblack">No flag information available</AdminMainText>
        </DashboardMainContainer>
      );
    }

    return (
      <DashboardMainContainer className="!bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-6 px-0">
            {/* System Flag Fields */}
            {flagInfo.type === 'system' && (
              <>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Flag Type</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.flagType || 'System-generated'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Rule ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.ruleId || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Rule Name</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.ruleName || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Rule Category</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.ruleCategory || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Rule Description</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.ruleDescription || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Trigger Data</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.triggerData || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Trigger Source</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.triggerSource || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Triggered On</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.triggeredOn ? formatDateTime(flagInfo.triggeredOn) : '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Repeat Flag Count</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.repeatFlagCount !== undefined ? flagInfo.repeatFlagCount : '—'}
                  </AdminDetailFieldValue>
                </div>
              </>
            )}

            {/* User Report Fields */}
            {flagInfo.type === 'user' && (
              <>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Report Type</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.reportType || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Reporter ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.reporterId || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Reporter Name</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.reporterName || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Report Category</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.reportCategory || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Report Reason</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.reportReason || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Report Notes</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.reportNotes || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Evidence Provided</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.evidenceProvided || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Report Timestamp</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.reportTimestamp ? formatDateTime(flagInfo.reportTimestamp) : '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Previous Reports</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.previousReports !== undefined ? flagInfo.previousReports : '—'}
                  </AdminDetailFieldValue>
                </div>
              </>
            )}

            {/* Admin Flag Fields */}
            {flagInfo.type === 'admin' && (
              <>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Flagged By</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.flaggedBy || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Flag Category</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.flagCategory || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Flag Reason</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.flagReason || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Flag Notes</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.flagNotes || '—'}
                  </AdminDetailFieldValue>
                </div>

                <div className="space-y-1">
                  <AdminDetailFieldLabel>Flag Timestamp</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                    {flagInfo.flagTimestamp ? formatDateTime(flagInfo.flagTimestamp) : '—'}
                  </AdminDetailFieldValue>
                </div>
              </>
            )}
        </div>
      </DashboardMainContainer>
    );
  };

  const renderLogs = () => {
    const archiveRow = getArchiveCaseByCaseId(caseId);
    const caseData = { ...(archiveRow || {}), ...(itemFromState || detail), ...(archiveCaseState || {}) };

    const formatAuditDateTime = (value) => {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      const datePart = date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      const timePart = date
        .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        .toLowerCase();
      return `${datePart}, ${timePart}`;
    };

    const inferUserType = (performedBy, rawRole = '') => {
      const role = String(rawRole || '').toLowerCase();
      const actor = String(performedBy || '').toLowerCase();
      if (role.includes('system') || actor === 'system') return 'System';
      if (role.includes('admin manager')) return 'Admin Manager';
      if (role.includes('moderator')) return 'Moderation';
      if (role.includes('support')) return 'Support';
      if (role.includes('customer') || actor.includes('customer') || actor.startsWith('user')) return 'Customer';
      if (role.includes('business') || actor.includes('business')) return 'Business';
      if (actor.startsWith('admin')) return 'Admin Manager';
      return 'Moderation';
    };

    const normalizeAuditAction = (log) => {
      const directAction = String(log.action || '').trim();
      if (directAction) return directAction;

      const eventType = String(log.eventType || '').trim();
      const eventDetail = String(log.eventDetail || '').trim();
      const source = String(log.source || '').toLowerCase();

      if (eventType) {
        const t = eventType.toLowerCase();
        if (t.includes('case created')) return 'Case Created';
        if (t.includes('status')) return 'Status Changed';
        if (t.includes('decision form') || t.includes('saved') || t.includes('draft')) return 'Decision Form Save';
        if (t.includes('decision')) return 'Decision Form Save';
        if (t.includes('transfer') || t.includes('escalat')) return 'Transfer Submitted';
        if (t.includes('note')) return 'Internal Note Saved';
        if (t.includes('closed') || t.includes('decided') || t.includes('archive')) return 'Status Changed';
        if (t.includes('appeal')) return 'Appeal Case Created';
      }

      if (eventDetail) {
        const d = eventDetail.toLowerCase();
        if (d.includes('new to active') || d.includes('active to decided') || d.includes('status')) return 'Status Changed';
        if (d.includes('saved') || d.includes('draft') || d.includes('decision')) return 'Decision Form Save';
        if (d.includes('transfer') || d.includes('escalat')) return 'Transfer Submitted';
        if (d.includes('note')) return 'Internal Note Saved';
        if (d.includes('closed') || d.includes('archive')) return 'Status Changed';
        if (d.includes('appeal')) return 'Appeal Case Created';
      }

      if (source.includes('decision')) return 'Decision Submitted';
      if (source.includes('queue')) return 'Status Changed';
      return eventType || eventDetail || '—';
    };

    const normalizeSection = (log) => {
      const directSection = String(log.section || '').trim();
      if (directSection) return directSection;

      const source = String(log.source || '').trim();
      if (!source) return 'Moderation Queue';
      const s = source.toLowerCase();
      if (s.includes('decision')) return 'Decision';
      if (s.includes('archive')) return 'Moderation Archive';
      if (s.includes('queue')) return 'Moderation Queue';
      return source;
    };

    const buildArchiveFallbackLogs = () => {
      const decidedBy = caseData.closedBy || 'Sarah K.';
      const closedOnIso = caseData.closedDate || '2026-01-29T10:14:00.000Z';
      const activeIso = new Date(new Date(closedOnIso).getTime() - 6 * 60 * 1000).toISOString();
      const decisionIso = new Date(new Date(closedOnIso).getTime() - 2 * 60 * 1000).toISOString();
      const createdIso = caseData.caseCreated || new Date(new Date(closedOnIso).getTime() - 26 * 60 * 60 * 1000).toISOString();

      return [
        {
          dateTime: createdIso,
          performedBy: 'System',
          userType: 'System',
          action: 'Case Created',
          section: 'Moderation Queue',
          summary: 'New moderation case entered the Queue',
        },
        {
          dateTime: activeIso,
          performedBy: decidedBy,
          userType: 'Moderation',
          action: 'Status Changed',
          section: 'Moderation Queue',
          summary: 'Case moved from New to In Progress',
        },
        {
          dateTime: decisionIso,
          performedBy: decidedBy,
          userType: 'Moderation',
          action: 'Confirm Decision',
          section: 'Decision',
          summary: `${caseData.decisionOutcome || caseData.enforcement || 'Decision'} recorded via Confirm Decision`,
        },
        {
          dateTime: closedOnIso,
          performedBy: decidedBy,
          userType: 'Moderation',
          action: 'Status Changed',
          section: 'Moderation Archive',
          summary: 'Case moved from In Progress to Decided and handed to Archive',
        },
      ];
    };

    if (isArchive && Array.isArray(caseData.auditLog) && caseData.auditLog.length > 0) {
      const normalizedLogs = caseData.auditLog
        .map((log) => ({
          dateTime: log.dateTime,
          performedBy: log.performedBy,
          userType: log.userType || inferUserType(log.performedBy, log.role || ''),
          action: log.action,
          section: log.section,
          summary: log.summary,
        }))
        .filter((row) => row.dateTime && !isEscalatedAuditLogEntry({ action: row.action }));
      normalizedLogs.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
      return (
        <div className="min-w-0 max-w-full">
          <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} overflow-hidden`}>
            <div className="w-full min-w-0 max-w-full">
              <div
                className="grid w-full max-w-full min-w-0 py-3 text-offblack border-b border-lightgray px-4 gap-x-2 sm:gap-x-3 gap-y-2"
                style={{ gridTemplateColumns: AUDIT_LOG_DETAIL_GRID }}
              >
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Date and Time</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Performed By</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>User Type</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Action</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Section</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Summary</AdminTableHeader>
                </div>
              </div>
              {normalizedLogs.map((log, idx) => (
                <div
                  key={idx}
                  className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                  style={{ gridTemplateColumns: AUDIT_LOG_DETAIL_GRID }}
                >
                  <div className="flex items-start min-w-0">
                    <AdminTableMeta className="break-words">{formatAuditDateTime(log.dateTime)}</AdminTableMeta>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.performedBy || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.userType || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.action || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.section || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.summary || '—'}</AdminTableBody>
                  </div>
                </div>
              ))}
            </div>
          </DashboardMainContainer>
        </div>
      );
    }

    const logsSource = scenario ? scenarioAuditLogs : auditLogs;
    let normalizedLogs = [...(logsSource || [])]
      .map((log) => {
        const performedBy = log.admin || log.performedBy || 'System';
        const action = normalizeAuditAction(log);
        const section = normalizeSection(log);
        const summary =
          log.summary ||
          log.eventDetail ||
          log.notes ||
          `${action} recorded for moderation case`;
        return {
          dateTime: log.timestamp || log.createdAt || log.date || null,
          performedBy,
          userType: inferUserType(performedBy, log.role || log.userType || ''),
          action,
          section,
          summary,
        };
      })
      .filter((row) => row.dateTime && !isEscalatedAuditLogEntry({ action: row.action }));

    if (isArchive && normalizedLogs.length === 0) {
      normalizedLogs = buildArchiveFallbackLogs();
    }

    normalizedLogs.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));

    return (
      <div className="min-w-0 max-w-full">
        <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} overflow-hidden`}>
          <div className="w-full min-w-0 max-w-full">
            <div
              className="grid w-full max-w-full min-w-0 py-3 text-offblack border-b border-lightgray px-4 gap-x-2 sm:gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: AUDIT_LOG_DETAIL_GRID }}
            >
              <div className="flex items-center min-w-0">
                <AdminTableHeader>Date and Time</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader>Performed By</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader>User Type</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader>Action</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader>Section</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader>Summary</AdminTableHeader>
              </div>
            </div>

            {normalizedLogs.length > 0 ? (
              normalizedLogs.map((log, idx) => (
                <div
                  key={idx}
                  className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                  style={{ gridTemplateColumns: AUDIT_LOG_DETAIL_GRID }}
                >
                  <div className="flex items-start min-w-0">
                    <AdminTableMeta className="break-words">{formatAuditDateTime(log.dateTime)}</AdminTableMeta>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.performedBy || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.userType || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.action || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.section || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.summary || '—'}</AdminTableBody>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center px-4">
                <AdminSupportingText className="text-mediumgray">No audit log entries</AdminSupportingText>
              </div>
            )}
          </div>
        </DashboardMainContainer>
      </div>
    );
  };

  const renderQueueAuditLog = () => {
    const rows = [...queueAuditLog].sort(
      (a, b) => new Date(a.dateTime || 0) - new Date(b.dateTime || 0)
    );
    const auditGrid = 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1.8fr) minmax(0, 1.1fr)';
    return (
      <div className="min-w-0 max-w-full">
        <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} overflow-hidden`}>
          <div className="w-full min-w-0 max-w-full overflow-x-auto">
            <div
              className="grid w-full max-w-full min-w-0 py-3 text-offblack border-b border-lightgray px-4 gap-x-2 sm:gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: auditGrid }}
            >
              <div className="flex items-center min-w-0">
                <AdminTableHeader>Date and Time</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader>Event</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader>Details</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader>Related Record</AdminTableHeader>
              </div>
            </div>
            {rows.length > 0 ? (
              rows.map((log, idx) => (
                <div
                  key={`${log.event}-${log.dateTime}-${idx}`}
                  className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                  style={{ gridTemplateColumns: auditGrid }}
                >
                  <div className="flex items-start min-w-0">
                    <AdminTableMeta className="break-words">
                      {formatQueueDateTime(log.dateTime)}
                    </AdminTableMeta>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.event || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words whitespace-pre-wrap">
                      {log.details || '—'}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-start min-w-0">
                    <AdminTableBody className="break-words">{log.relatedRecord || '—'}</AdminTableBody>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center">
                <AdminSupportingText className="text-mediumgray">
                  No audit logs found
                </AdminSupportingText>
              </div>
            )}
          </div>
        </DashboardMainContainer>
      </div>
    );
  };

  const renderAdminNotes = () => {
    const notesGrid = 'minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(88px, auto)';
    const sortedNotes = [...queueNotes].sort(
      (a, b) => new Date(b.addedOn || 0) - new Date(a.addedOn || 0)
    );
    return (
      <div>
        {notesEditable ? (
          <div className="mb-4 flex justify-end">
            <AdminHeaderAction
              type="button"
              onClick={() => setNoteEditor({ mode: 'add', draft: '' })}
            >
              Add Note
            </AdminHeaderAction>
          </div>
        ) : null}
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div
            className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4"
            style={{ gridTemplateColumns: notesGrid }}
          >
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Note</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Added By</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Added On</AdminTableHeader>
            </div>
            <div className="flex items-center justify-center min-w-0">
              <AdminTableHeader>Actions</AdminTableHeader>
            </div>
          </div>
          {sortedNotes.length > 0 ? (
            sortedNotes.map((note) => (
              <div
                key={note.id}
                className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4"
                style={{ gridTemplateColumns: notesGrid }}
              >
                <div className="flex items-start min-w-0">
                  <AdminTableBody className="whitespace-pre-wrap break-words">
                    {noteBody(note)}
                  </AdminTableBody>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableBody emphasis>{note.addedBy}</AdminTableBody>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableMeta>{formatComplianceNotesDateTime(note.addedOn)}</AdminTableMeta>
                </div>
                <div className="flex items-start justify-center gap-3 min-w-0">
                  <AdminLinkAction type="button" onClick={() => setViewNote({ ...note, text: noteBody(note) })}>
                    View
                  </AdminLinkAction>
                  {notesEditable ? (
                    <AdminLinkAction
                      type="button"
                      onClick={() =>
                        setNoteEditor({
                          mode: 'edit',
                          noteId: note.id,
                          existing: noteBody(note),
                          draft: '',
                        })
                      }
                    >
                      Edit
                    </AdminLinkAction>
                  ) : null}
                </div>
              </div>
            ))
          ) : (
            <div className="py-8 text-center">
              <AdminSupportingText className="text-mediumgray">No notes yet</AdminSupportingText>
            </div>
          )}
        </DashboardMainContainer>
      </div>
    );
  };

  // Get available enforcement actions based on item type
  const getAvailableActions = () => {
    const allActions = {
      'Hide': { label: 'Hide', color: 'bg-warmyellow text-white', availableFor: ['Posts', 'Reviews', 'Messages', 'Quote'] },
      'Remove': { label: 'Remove', color: 'bg-alertred text-white', availableFor: ['Posts', 'Reviews', 'Messages', 'Quote'] },
      'Restrict': { label: 'Restrict', color: 'bg-warmyellow text-white', availableFor: ['Posts', 'Reviews', 'Messages', 'Quote', 'Business Profiles', 'Customer Profiles'] },
      'Lock Profile': { label: 'Lock Profile', color: 'bg-alertred text-white', availableFor: ['Business Profiles', 'Customer Profiles'] },
      'Suspend Account': { label: 'Suspend Account', color: 'bg-alertred text-white', availableFor: ['Business Profiles', 'Customer Profiles'] },
      'Reject Document': { label: 'Reject Document', color: 'bg-alertred text-white', availableFor: ['Documents & Licences'] },
      'Request Re-submission': { label: 'Request Re-submission', color: 'bg-primaryblue text-white', availableFor: ['Documents & Licences'] },
      'Close Case': { label: 'Close Case', color: 'bg-freshgreen text-white', availableFor: ['Posts', 'Reviews', 'Messages', 'Quote', 'Business Profiles', 'Customer Profiles', 'Documents & Licences', 'Misconduct', 'System Flags', 'Flag'] },
      'Escalate': { label: 'Escalate', color: 'bg-warmyellow text-white', availableFor: ['Posts', 'Reviews', 'Messages', 'Quote', 'Business Profiles', 'Customer Profiles', 'Documents & Licences', 'Misconduct', 'System Flags', 'Flag'] },
    };

    const itemType = detail?.itemType || (scenario?.quote ? 'Quote' : scenario?.conversation ? 'Messages' : 'Flag');
    const actions = Object.entries(allActions)
      .filter(([_, action]) => action.availableFor.includes(itemType))
      .map(([key, action]) => ({ key, ...action }));
    
    // Always ensure Close Case and Escalate are included
    const hasCloseCase = actions.some(a => a.key === 'Close Case');
    const hasEscalate = actions.some(a => a.key === 'Escalate');
    
    if (!hasCloseCase) {
      actions.push({ key: 'Close Case', label: 'Close Case', color: 'bg-freshgreen text-white' });
    }
    if (!hasEscalate) {
      actions.push({ key: 'Escalate', label: 'Escalate', color: 'bg-warmyellow text-white' });
    }
    
    return actions;
  };

  const handleEnforcementAction = (actionKey) => {
    const actionLabels = {
      'Hide': 'Hide',
      'Remove': 'Remove',
      'Restrict': 'Restrict',
      'Lock Profile': 'Lock Profile',
      'Suspend Account': 'Suspend Account',
      'Reject Document': 'Reject Document',
      'Request Re-submission': 'Request Re-submission',
      'Close Case': 'Close Case',
      'Escalate': 'Escalate',
    };

    const actionLabel = actionLabels[actionKey] || actionKey;
    const confirmMessage = `Are you sure you want to ${actionLabel.toLowerCase()} this item?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const timestamp = new Date().toISOString();
    const adminName = 'Admin: R.B.'; // In real app, get from auth context

    // Update item status based on action
    let newStatus = detail.queueStatus;
    let newSeverity = detail.severity;
    
    switch (actionKey) {
      case 'Close Case':
        newStatus = 'Closed';
        break;
      case 'Escalate':
        newStatus = 'Escalated';
        if (detail.severity === 'Level 1 — Low') newSeverity = 'Level 2 — Medium';
        else if (detail.severity === 'Level 2 — Medium') newSeverity = 'Level 3 — High';
        else if (detail.severity === 'Level 3 — High') newSeverity = 'Level 4 — Critical';
        break;
      case 'Hide':
      case 'Remove':
      case 'Restrict':
      case 'Lock Profile':
      case 'Suspend Account':
      case 'Reject Document':
        newStatus = 'In Progress';
        break;
      case 'Request Re-submission':
        newStatus = 'Assigned';
        break;
    }

    // Update detail state
    setDetail(prev => ({
      ...prev,
      queueStatus: newStatus,
      severity: newSeverity,
    }));

    // Add audit log entry
    const auditEntry = {
      id: `log-${Date.now()}`,
      timestamp: timestamp,
      admin: adminName,
      eventType: 'Enforcement Action Applied',
      eventDetail: `${actionLabel} action applied`,
      previousValue: detail.queueStatus,
      newValue: newStatus,
      notes: `Enforcement action: ${actionLabel}`,
    };

    try {
      recordCaseActivity({
        caseRef: queueCase?.caseId || detail?.caseId || detail?.itemId || 'moderation',
        module: 'Moderation',
        sourceModuleRef: 'moderation',
        changeType: 'Enforcement Action Applied',
        activityDetails: `${adminName}; ${actionLabel} action applied`,
        changedBy: adminName,
        changedOnIso: timestamp,
      });
      if (actionKey === 'Hide' || actionKey === 'Remove') {
        emitContentPublicationFromActions(
          [{ actionType: actionKey.toLowerCase(), ruleName: actionLabel }],
          adminName,
        );
      }
    } catch {
      /* history recording failure must not reverse the source save */
    }

    // Add to audit logs (this would normally be done via API)
    // For now, we'll just show a success message
    alert(`Action "${actionLabel}" has been applied. An entry has been added to Audit log.`);
  };

  const renderContext = () => {
    const caseData = itemFromState || detail;

    const formatMemberSince = (dateString) => {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatLastActive = (dateString) => {
      if (!dateString) return '—';
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
      return `${datePart} · ${timePart}`;
    };

    const b = scenario?.business;
    const cust = scenario?.profileType === 'Customer' ? scenario.customer : (scenario?.conversation || scenario?.quote) ? scenario.customer : null;
    const customerData = cust ? {
      name: cust.name,
      customerId: cust.customerId,
      memberSince: cust.memberSince || '—',
      lastActive: cust.lastActive ? (() => {
        const d = new Date(cust.lastActive);
        return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
      })() : '—',
      accountStatus: cust.accountStatus === 'Active' ? 'Active' : (cust.accountStatus || '—'),
      blockStatus: cust.blockStatus,
    } : {
      name: 'Jane Miller',
      customerId: 'CUS-11832',
      memberSince: '17 Nov 2023',
      lastActive: '17 Nov 2025 · 01:12 PM',
      accountStatus: 'Active',
      blockStatus: null,
    };

    const businessData = b ? {
      name: b.businessName || b.name,
      businessId: b.businessId,
      tradeCategory: formatBusinessCategory(b.category),
      verificationStatus: (b.verificationStatus || '').toLowerCase() === 'verified' ? 'Verified' : (b.verificationStatus || '—'),
      accountStatus: (b.status || b.accountStatus || '').toLowerCase() === 'active' ? 'Active' : (b.status || b.accountStatus || '—'),
      blockStatus: b.blockStatus,
    } : {
      name: 'Melbourne Plumbing Co.',
      businessId: 'BUS-4521',
      tradeCategory: 'Plumbing',
      verificationStatus: 'Verified',
      accountStatus: 'Active',
      blockStatus: null,
    };

    return (
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer Context Card */}
          <DashboardMainContainer className="!bg-white">
            <div className="p-6">
              <AdminPanelTitle className="text-offblack mb-4">CUSTOMER (individual actor)</AdminPanelTitle>
              <div className="space-y-4">
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Customer ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{customerData.customerId}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Name</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{customerData.name}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Member Since</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{customerData.memberSince}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Last Active</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{customerData.lastActive}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{customerData.accountStatus}</AdminDetailFieldValue>
                </div>
                {(customerData.blockStatus != null && customerData.blockStatus !== undefined) && (
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Block Status</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{customerData.blockStatus || '—'}</AdminDetailFieldValue>
                  </div>
                )}
              </div>
            </div>
          </DashboardMainContainer>

          {/* Business Context Card */}
          <DashboardMainContainer className="!bg-white">
            <div className="p-6">
              <AdminPanelTitle className="text-offblack mb-4">BUSINESS (regulated entity)</AdminPanelTitle>
              <div className="space-y-4">
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Business ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{businessData.businessId}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Business Name</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{businessData.name}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Trade Category</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{businessData.tradeCategory}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Verification Status</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{businessData.verificationStatus}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{businessData.accountStatus}</AdminDetailFieldValue>
                </div>
                {(businessData.blockStatus != null && businessData.blockStatus !== undefined) && (
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Block Status</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{businessData.blockStatus || '—'}</AdminDetailFieldValue>
                  </div>
                )}
              </div>
            </div>
          </DashboardMainContainer>
        </div>
      </div>
    );
  };

  const toggleEnforcementRowActions = (eventId) => {
    setEnforcementRowActionsOpen((prev) => {
      if (prev[eventId]) return {};
      return { [eventId]: true };
    });
  };

  // Render Decision Record for Archive (read-only) — DECISION RECORD — MODERATION ARCHIVE (FINAL STRUCTURE)
  const renderDecisionAndEnforcement = () => {
    const archiveRow = getArchiveCaseByCaseId(caseId);
    const caseData = { ...(archiveRow || {}), ...(itemFromState || detail), ...(archiveCaseState || {}) };
    const c = scenario?.case;

    // 1. Decision Overview
    const scenarioViolationMap = { 1: 'Copyright', 2: 'Policy', 3: 'Misrepresentation', 4: 'Spam', 5: 'Other' };
    const rawViolation =
      (scenario ? (scenarioViolationMap[scenario.scenarioKey] || caseData.caseReason) : null) ||
      caseData.violationType ||
      caseData.violation ||
      caseData.caseReason;
    const violationTypeDisplay = rawViolation === 'Copyright' ? 'Copyright' :
      rawViolation === 'Policy' ? 'Policy Breach' :
      rawViolation === 'Misrepresentation' ? 'Misleading Information' :
      rawViolation === 'Spam' ? 'Spam Account' :
      rawViolation === 'Scam' ? 'Fraud / Scam' :
      rawViolation === 'Harassment' || rawViolation === 'Abuse' ? 'User Misconduct' :
      rawViolation === 'Compliance' ? 'Licence / Qualification' :
      rawViolation === 'No Violation' ? 'No Violation' :
      rawViolation || '—';

    const severityRaw = c?.severity || caseData.severity;
    const severityDisplay = severityRaw ? (String(severityRaw).charAt(0).toUpperCase() + String(severityRaw).slice(1).toLowerCase()) : '—';

    const rawEnforcement =
      c?.enforcement ||
      c?.outcome ||
      caseData.enforcementApplied ||
      caseData.enforcement ||
      caseData.finalDecision;
    const enforcementDisplay = rawEnforcement === 'No Violation' || rawEnforcement == null || rawEnforcement === '' ? 'No Action' :
      String(rawEnforcement).toLowerCase().includes('escalat') || c?.escalatedTo ? 'Escalated' :
      String(rawEnforcement).toLowerCase().includes('suspend') ? 'Suspend Account' :
      String(rawEnforcement).toLowerCase().includes('remove') || String(rawEnforcement).toLowerCase().includes('content removed') ? 'Remove Content' :
      String(rawEnforcement).toLowerCase().includes('hide') ? 'Hide Content' :
      String(rawEnforcement).toLowerCase().includes('warning') ? 'Warning' :
      rawEnforcement || 'No Action';

    const escalatedToRaw = c?.escalatedTo || caseData.escalatedTo;
    const escalatedToDisplay = escalatedToRaw === 'Compliance' ? 'Compliance' :
      escalatedToRaw === 'Fraud & Security' || escalatedToRaw === 'Security & Fraud' ? 'Fraud & Security' :
      escalatedToRaw || '—';

    const affectedAccountNotified = caseData.notifyAffectedAccount ?? caseData.notifyAffectedParty ?? true;
    const reportingPartyNotified = caseData.notifyReportingParty ?? caseData.notifyOtherParty ?? false;

    const decidedBy = c?.closedBy || caseData.closedBy || closedBy || '—';
    const decisionDateRaw = caseData.decisionDate || c?.closedDate || caseData.closedDate;
    const decisionDate = decisionDateRaw ? formatDateTime(decisionDateRaw) : '—';

    const policyConfirmation = caseData.confirmed === true || caseData.policyConfirmed === true ? 'Confirmed' : (caseData.confirmed === false || caseData.policyConfirmed === false ? 'Not Confirmed' : 'Confirmed');
    const caseStatusDisplay = caseData.caseStatus || (c?.status && c.status !== 'New' ? 'Closed' : 'Closed');

    const finalDecisionDisplay = caseData.finalDecision || (enforcementDisplay === 'No Action' ? 'Closed in Moderation' : 'Closed in Moderation');

    const transferCase = caseData.transferCase === true || Boolean(caseData.transferTo || caseData.transferReason || caseData.transferNotes);
    const transferToDisplay = caseData.transferTo || caseData.escalatedTo || '—';
    const transferReasonDisplay = caseData.transferReason || (transferCase ? 'Requires Specialist Review' : '—');
    const transferNotesDisplay = caseData.transferNotes || '—';

    const Field = ({ label, value }) => (
      <div className="space-y-1">
        <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{value}</AdminDetailFieldValue>
      </div>
    );

    return (
      <div>
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Column 1 */}
            <div className="space-y-4">
              <Field label="Violation Type" value={violationTypeDisplay} />
              <Field label="Severity" value={severityDisplay} />
              <Field label="Final Decision" value={finalDecisionDisplay} />
              <Field label="Enforcement Applied" value={enforcementDisplay} />
              <Field label="Affected Account Notified" value={affectedAccountNotified ? 'Yes' : 'No'} />
              <Field label="Reporting Party Notified" value={reportingPartyNotified ? 'Yes' : 'No'} />
              <Field label="Policy Confirmed" value={policyConfirmation} />
              <Field label="Decided By" value={decidedBy} />
              <Field label="Decision Date" value={decisionDate} />
              <Field label="Case Status" value={caseStatusDisplay} />
            </div>

            {/* Column 2 */}
            <div className="space-y-4">
              <Field label="Transfer Case" value={transferCase ? 'Yes' : 'No'} />
              <Field label="Transfer To" value={transferCase ? escalatedToDisplay || transferToDisplay : '—'} />
              <Field label="Transfer Reason" value={transferCase ? transferReasonDisplay : '—'} />
              <Field label="Transfer Notes" value={transferCase ? transferNotesDisplay : '—'} />
            </div>
          </div>
        </DashboardMainContainer>
      </div>
    );
  };

  const renderEnforcementAndEscalation = () => (
    <ModerationDecisionTab scenario={scenario} itemFromState={itemFromState} />
  );

  const renderEnforcementActions = () => {
    const availableActions = getAvailableActions();

    return (
      <DashboardMainContainer className="!bg-white">
          {availableActions && availableActions.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {availableActions.map((action) => (
                <AdminRowAction
                  key={action.key}
                  onClick={() => handleEnforcementAction(action.key)}
                >
                  {action.label}
                </AdminRowAction>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <AdminSupportingText>
                No enforcement actions available for this item type: {detail.itemType}
              </AdminSupportingText>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-lightgray">
            <AdminPanelTitle className="text-offblack mb-3">
              Action Information
            </AdminPanelTitle>
            <div className="space-y-2">
              <AdminSupportingText>• All enforcement actions are permanent and cannot be undone without admin approval</AdminSupportingText>
              <AdminSupportingText>• Each action automatically creates an audit log entry with timestamp and admin details</AdminSupportingText>
              <AdminSupportingText>• Actions may affect the item status, severity level, or queue status</AdminSupportingText>
              <AdminSupportingText>• Review the Audit log tab to see all action history</AdminSupportingText>
            </div>
          </div>
      </DashboardMainContainer>
    );
  };

  const renderAppeal = () => {
    const archiveRow = getArchiveCaseByCaseId(caseId);
    const caseData = { ...(archiveRow || {}), ...(itemFromState || detail), ...(archiveCaseState || {}) };
    const contentType = caseData.contentType || '—';
    const appealStatus = caseData.appealStatus || 'No Appeal';
    const originalClosed = formatDateTime(caseData.closedDate || scenario?.case?.closedDate);
    const showCreateForm = appealStatus === 'No Appeal';

    const renderOriginalContentField = () => {
      if (contentType === 'Post') {
        return (
          <div className="space-y-1">
            <AdminDetailFieldLabel>Original Post ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{caseData.postId || caseData.contentId || '—'}</AdminDetailFieldValue>
          </div>
        );
      }
      if (contentType === 'Review') {
        return (
          <div className="space-y-1">
            <AdminDetailFieldLabel>Original Review ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{caseData.reviewId || caseData.contentId || '—'}</AdminDetailFieldValue>
          </div>
        );
      }
      if (contentType === 'Message') {
        return (
          <div className="space-y-1">
            <AdminDetailFieldLabel>Original Message ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{caseData.messageId || caseData.contentId || '—'}</AdminDetailFieldValue>
          </div>
        );
      }
      return null;
    };

    if (!showCreateForm) {
      return (
        <div className="w-full max-w-md">
          <div className="space-y-1">
            <AdminSupportingText className="text-offblack">
              Appeal case already created.
            </AdminSupportingText>
            <AdminSupportingText className="text-offblack">
              Archive closed and unchanged.
            </AdminSupportingText>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full max-w-md">
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="space-y-6">
            <div className="space-y-5">
                <div className="flex flex-col gap-4 w-full">
                  <div className="space-y-1 w-full">
                    <AdminDetailFieldLabel>Requested By</AdminDetailFieldLabel>
                    <div className="relative w-full min-w-0">
                      <select
                        value={appealForm.requestedBy}
                        onChange={(e) => setAppealForm((prev) => ({ ...prev, requestedBy: e.target.value }))}
                        className={adminWizardSelectClasses()}
                      >
                        <option value="">Select</option>
                        <option value="Customer">Customer</option>
                        <option value="Business">Business</option>
                        <option value="Moderation">Moderation</option>
                      </select>
                      <span
                        className="pointer-events-none absolute right-2.5 top-1/2 z-[5] flex h-3.5 w-3.5 shrink-0 -translate-y-1/2 items-center justify-center"
                        aria-hidden
                      >
                        <i className="fi fi-rs-angle-down block !text-[14px] !leading-none text-offblack" />
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1 w-full">
                    <AdminDetailFieldLabel>Appeal Summary</AdminDetailFieldLabel>
                    <textarea
                      value={appealForm.appealSummary}
                      onChange={(e) => setAppealForm((prev) => ({ ...prev, appealSummary: e.target.value }))}
                      className={`${adminFormTextareaClasses()} w-full min-h-[100px]`}
                      placeholder="Provide the context"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Original Case ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{caseData.caseId || caseId || '—'}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Original Violation Type</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{caseData.violationType || '—'}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Original Enforcement</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{caseData.enforcementApplied || '—'}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Original Closed</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{originalClosed}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Original Account</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{caseData.accountName || '—'}</AdminDetailFieldValue>
                  </div>
                  {renderOriginalContentField()}
                </div>

                <div className="flex justify-end">
                  <AdminHeaderAction
                  onClick={() =>handleCreateAppealCase(caseData)}
                  >
                    Create Case</AdminHeaderAction>
                </div>
            </div>
          </div>
        </DashboardMainContainer>
      </div>
    );
  };

  const tabs = useMemo(
    () => [
      { id: 'overview', label: 'Overview' },
      { id: 'content', label: 'Content' },
      { id: 'enforcement', label: 'Decision' },
      { id: 'notes', label: 'Internal Notes' },
      { id: 'logs', label: 'Audit Log' },
    ],
    []
  );

  const renderActiveTab = () => {
    if (isArchive) {
      switch (activeTab) {
        case 'overview':
          return renderOverview();
        case 'content':
          return renderContentAndEvidence();
        case 'enforcement':
          return (
            <ModerationDecisionTab
              scenario={scenario}
              itemFromState={archiveCase}
              archivedRecord={buildArchivedDecision(archiveCase, scenario)}
            />
          );
        case 'notes':
          return renderAdminNotes();
        case 'logs':
          return renderQueueAuditLog();
        default:
          return renderOverview();
      }
    }
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'content':
        return renderContentAndEvidence();
      case 'enforcement':
        return (
          <ModerationDecisionTab
            scenario={scenario}
            itemFromState={{
              ...queueCase,
              status: overviewAssignment.caseStatus,
            }}
            caseStatus={overviewAssignment.caseStatus}
            assignedHandler={overviewAssignment.assignedHandler}
            onFinalSave={handleFinalDecisionSave}
            onProgressSave={handleProgressSave}
            onAssignToMe={handleAssignToMe}
            initialDraft={getModerationCaseOverride(queueCase.caseId)?.decisionDraft}
          />
        );
      case 'notes':
        return renderAdminNotes();
      case 'logs':
        return renderQueueAuditLog();
      default:
        return renderOverview();
    }
  };

  const itemTitle = resolveCaseHeader(isArchive ? archiveCase : queueCase, scenario);
  const caseId = scenario?.case?.caseId || itemFromState?.caseId || detail.itemId || itemId || '—';
  const closedBy = itemFromState?.closedBy || scenario?.case?.closedBy || '—';

  // Render Overview tab (Archive only) — four columns per spec
  const renderCaseSummary = () => {
    const caseData = itemFromState || detail;
    const c = scenario?.case;
    const f = scenario?.flag;
    const archiveRow = getArchiveCaseByCaseId(caseId);
    const d = { ...(archiveRow || {}), ...caseData, ...(archiveCaseState || {}) };

    const scenarioViolationMap = { 1: 'Copyright', 2: 'Policy', 3: 'Misrepresentation', 4: 'Spam', 5: 'Other' };
    const violation = (scenario ? (scenarioViolationMap[scenario.scenarioKey] || caseData.caseReason) : null) || d.violationType || caseData.violation || caseData.caseReason || '—';
    const violationDisplay = violation === 'Scam' ? 'Scam / Fraud' :
                            violation === 'Harassment' ? 'Abuse / harassment' :
                            violation === 'Abuse' ? 'Abuse / harassment' :
                            violation === 'Spam' ? 'Spam' :
                            violation === 'Policy' ? 'Misrepresentation' :
                            violation === 'Copyright' ? 'Intellectual property' :
                            violation === 'Compliance' ? 'Other' :
                            violation;

    const source = d.caseSource || (scenario ? (f?.source || 'Customer') : (caseData.source || caseData.flagSource || detail.flagType || 'System-Flagged'));
    const normalizedSource = source === 'System-Flagged' ? 'System' :
                            source === 'User-Reported' ? 'Customer' :
                            source === 'Business-Reported' ? 'Business' :
                            source === 'Admin-Escalated' ? 'Admin' :
                            source === 'System' ? 'System' :
                            source === 'Customer' ? 'Customer' :
                            source === 'Business' ? 'Business' :
                            source === 'Admin' ? 'Admin' :
                            source === 'Flag' ? 'Flag' :
                            source.includes('System') ? 'System' :
                            source.includes('Customer') || source.includes('User') ? 'Customer' :
                            source.includes('Business') ? 'Business' :
                            source.includes('Admin') ? 'Admin' : source;

    const finalDecisionFromScenario = scenario
      ? (c?.outcome === 'No Violation' ? 'No Action' : (c?.enforcement || c?.outcome || 'No Action'))
      : null;
    const finalDecisionDisplay = d.finalDecision || finalDecisionFromScenario || '—';

    const enforcementFromScenario = scenario
      ? (c?.outcome !== 'No Violation' ? 'Applied' : 'Not Applied')
      : null;
    const enforcementDisplay = d.enforcementApplied != null && d.enforcementApplied !== ''
      ? d.enforcementApplied
      : (enforcementFromScenario ?? (caseData.enforcementApplied === 'Yes' ? 'Applied' : 'Not Applied'));

    const contentType = scenario ? (c?.contentType || 'Post') : (d.contentType || caseData.contentType || caseData.type || detail.itemType || 'Message');
    const normalizedContentType = contentType === 'Posts' ? 'Post' :
                                  contentType === 'Reviews' ? 'Review' :
                                  contentType === 'Messages' ? 'Message' :
                                  contentType === 'Documents & Licences' ? 'Document' :
                                  contentType === 'Direct Quote' || contentType === 'Group Quote' ? 'Quote' :
                                  contentType;

    const scenarioContentId = scenario?.post?.postId || scenario?.review?.reviewId || scenario?.conversation?.threadId;
    const contentIdDisplay = d.contentId || scenarioContentId || caseData.postId || caseData.contentId || '—';
    const contentTitleOrExcerpt =
      d.contentTitle ||
      d.contentExcerpt ||
      scenario?.post?.title ||
      scenario?.review?.fullText ||
      scenario?.review?.body ||
      scenario?.review?.text ||
      scenario?.conversation?.preview ||
      caseData.postTitle ||
      itemTitle ||
      '—';

    const accountStatusVal = (d.accountStatus || scenario?.business?.status || caseData.accountStatus || 'active')
      ? String(d.accountStatus || scenario?.business?.status || caseData.accountStatus || 'active').charAt(0).toUpperCase() +
        String(d.accountStatus || scenario?.business?.status || caseData.accountStatus || 'active').slice(1).toLowerCase()
      : '—';
    const accountNameVal = d.accountName || scenario?.business?.businessName || scenario?.customer?.name || caseData.businessName || caseData.accountName || '—';
    const businessIdVal = d.businessId || scenario?.business?.businessId || caseData.businessId;
    const customerIdVal = d.customerId || scenario?.customer?.customerId || caseData.partiesInvolved?.find((p) => p.type === 'Customer')?.id;
    const accountTypeVal = d.accountType || (customerIdVal && !businessIdVal ? 'Customer' : businessIdVal || customerIdVal ? 'Business' : '—');
    const accountIdVal = d.accountId || businessIdVal || customerIdVal || '—';

    const flagId = d.flagId || f?.flagId || '—';
    const businessPublicProfileHref = '/poster-profile/?id=96';
    const memberSinceRaw = d.memberSince || scenario?.business?.joined || scenario?.customer?.joined || caseData.joined || caseData.memberSince;
    const memberSince = memberSinceRaw
      ? (typeof memberSinceRaw === 'string' && memberSinceRaw.length === 10
        ? new Date(`${memberSinceRaw}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : formatNoteDateTime(memberSinceRaw))
      : '—';

    const quoteId = scenario?.quote?.quoteId || scenario?.quote?.id || caseData.quoteId;
    const threadId = scenario?.conversation?.threadId || scenario?.conversation?.id || caseData.threadId || caseData.messageId;
    let flagIdDetailPath = null;
    if (flagId && flagId !== '—') {
      if (normalizedContentType === 'Post' && contentIdDisplay && contentIdDisplay !== '—') {
        flagIdDetailPath = `/admin/posts/${contentIdDisplay}`;
      } else if (normalizedContentType === 'Review' && contentIdDisplay && contentIdDisplay !== '—') {
        flagIdDetailPath = `/admin/reviews/${contentIdDisplay}`;
      } else if (normalizedContentType === 'Message' && threadId) {
        flagIdDetailPath = `/admin/communication/all/${threadId}`;
      } else if ((normalizedContentType === 'Quote' || contentType === 'Direct Quote' || contentType === 'Group Quote') && (quoteId || contentIdDisplay) && (quoteId || contentIdDisplay) !== '—') {
        flagIdDetailPath = `/admin/communication/all/${quoteId || contentIdDisplay}`;
      }
    }

    const customerDetailHref = customerIdVal
      ? `/admin/users/customers/${String(customerIdVal).replace(/^(CUS-|CU-)/i, '')}`
      : null;
    const businessDetailHref = businessIdVal
      ? `/admin/users/businesses/${String(businessIdVal).replace(/^(BUS-|BU-)/i, '')}`
      : null;

    const closedByDisplay = d.closedBy || c?.closedBy || caseData.closedBy || closedBy || '—';

    const caseCreatedDisplay = formatDateTime(d.caseCreated || c?.created || caseData.created);
    const closedOnDisplay = formatDateTime(d.closedDate || c?.closedDate || caseData.closedDate);
    const archiveCaseStatus = normalizeQueueStatus(
      d.status || c?.status || caseData.status || caseData.caseStatus || 'Decided'
    );

    const renderAccountName = () => {
      if (accountTypeVal === 'Customer' && customerDetailHref) {
        return <AdminLinkAction to={customerDetailHref}>{accountNameVal}</AdminLinkAction>;
      }
      if (accountTypeVal === 'Business' && businessPublicProfileHref) {
        return (
          <AdminLinkAction href={businessPublicProfileHref} target="_blank" rel="noopener noreferrer">
            {accountNameVal}
          </AdminLinkAction>
        );
      }
      return accountNameVal;
    };

    const renderAccountId = () => {
      if (accountTypeVal === 'Customer' && customerDetailHref) {
        return <AdminLinkAction to={customerDetailHref}>{accountIdVal}</AdminLinkAction>;
      }
      if (accountTypeVal === 'Business' && businessDetailHref) {
        return <AdminLinkAction to={businessDetailHref}>{accountIdVal}</AdminLinkAction>;
      }
      return accountIdVal;
    };

    const renderColumn4 = () => {
      if (normalizedContentType === 'Post') {
        const pid = d.postId || d.contentId || contentIdDisplay;
        const ptitle = d.postTitle || d.contentTitle || scenario?.post?.title || '—';
        return (
          <>
            <div className="space-y-1">
              <AdminDetailFieldLabel>Post ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{pid || '—'}</AdminDetailFieldValue>
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel>Post Title</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{ptitle}</AdminDetailFieldValue>
            </div>
          </>
        );
      }
      if (normalizedContentType === 'Review') {
        const rid = d.reviewId || d.contentId || contentIdDisplay;
        const rex = d.reviewExcerpt || d.contentExcerpt || scenario?.review?.fullText || scenario?.review?.body || scenario?.review?.text || '—';
        return (
          <>
            <div className="space-y-1">
              <AdminDetailFieldLabel>Review ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{rid || '—'}</AdminDetailFieldValue>
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel>Review Excerpt</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{rex}</AdminDetailFieldValue>
            </div>
          </>
        );
      }
      if (normalizedContentType === 'Message') {
        const mid = d.messageId || d.contentId || contentIdDisplay;
        const mex = d.messageExcerpt || d.contentExcerpt || scenario?.conversation?.preview || '—';
        return (
          <>
            <div className="space-y-1">
              <AdminDetailFieldLabel>Message ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{mid || '—'}</AdminDetailFieldValue>
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel>Message Excerpt</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{mex}</AdminDetailFieldValue>
            </div>
          </>
        );
      }
      return <div className="text-sm text-mediumgray">—</div>;
    };

    return (
      <div>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Column 1 — case & content */}
            <div className="space-y-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Case ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{caseId}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Flag ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                  {flagIdDetailPath ? (
                    <AdminLinkAction to={flagIdDetailPath} state={{ activeTab: 'flags' }}>{flagId}</AdminLinkAction>
                  ) : (
                    flagId
                  )}
                </AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Content Type</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{normalizedContentType}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Content ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{contentIdDisplay}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Content Title / Content Excerpt</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{contentTitleOrExcerpt}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Case Source</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{normalizedSource}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Flag Reason</AdminDetailFieldLabel>
        <AdminDetailFieldValue>
                  {d.flagReason || f?.reason || f?.trigger || caseData.flagReason || caseData.reason || '—'}
                </AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Case Created</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{caseCreatedDisplay}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Closed On</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{closedOnDisplay}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Case Status</AdminDetailFieldLabel>
                <AdminDetailFieldValue>
                  <AdminStatusBadge status={archiveCaseStatus} label={archiveCaseStatus} />
                </AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Violation Type</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{violationDisplay}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Final Decision</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{finalDecisionDisplay}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Enforcement Applied</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{enforcementDisplay}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Closed By</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{closedByDisplay}</AdminDetailFieldValue>
              </div>
            </div>

            {/* Column 2 — account */}
            <div className="space-y-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Account Name</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{renderAccountName()}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Account Type</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{accountTypeVal}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Account ID</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{renderAccountId()}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{accountStatusVal}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Member Since</AdminDetailFieldLabel>
        <AdminDetailFieldValue>{memberSince}</AdminDetailFieldValue>
              </div>
            </div>

            {/* Column 3 — content type fields */}
            <div className="space-y-4">{renderColumn4()}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="tp-container-main">
      <div>
        {/* Screen 2 header — title only (metadata lives in Overview) */}
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <AdminPanelTitle className="text-offblack">{itemTitle}</AdminPanelTitle>
        </DashboardMainContainer>

        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <AdminTabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className={adminDetailTabContentClassName(activeTab)}>
            {renderActiveTab()}
          </div>
        </DashboardMainContainer>
        </div>
      </div>

      {!isArchive && noteEditor ? (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setNoteEditor(null)}
        >
          <div
            className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <AdminPanelTitle className="text-offblack">
                {noteEditor.mode === 'add' ? 'Add Note' : 'Edit'}
              </AdminPanelTitle>
              <button
                type="button"
                onClick={() => setNoteEditor(null)}
                className="text-offblack hover:text-primaryblue shrink-0"
                aria-label="Close"
              >
                <i className="fi fi-rr-cross text-base" />
              </button>
            </div>
            {noteEditor.mode === 'edit' && noteEditor.existing ? (
              <AdminDetailFieldValue className="whitespace-pre-wrap mb-3">
                {noteEditor.existing}
              </AdminDetailFieldValue>
            ) : null}
            <textarea
              value={noteEditor.draft}
              onChange={(e) => setNoteEditor((prev) => ({ ...prev, draft: e.target.value }))}
              rows={4}
              className={`${adminFormTextareaClasses()} w-full min-h-[100px]`}
            />
            <div className="mt-6 flex justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={() => setNoteEditor(null)}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction type="button" onClick={saveQueueNote}>
                Save
              </AdminHeaderAction>
            </div>
          </div>
        </div>
      ) : null}

      {viewNote ? (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setViewNote(null)}
        >
          <div
            className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <AdminPanelTitle className="text-offblack">View</AdminPanelTitle>
              <button
                type="button"
                onClick={() => setViewNote(null)}
                className="text-offblack hover:text-primaryblue shrink-0"
                aria-label="Close"
              >
                <i className="fi fi-rr-cross text-base" />
              </button>
            </div>
            <AdminDetailFieldValue className="whitespace-pre-wrap">
              {viewNote.text}
            </AdminDetailFieldValue>
          </div>
        </div>
      ) : null}

    </AdminLayout>
  );
};

export default ModerationItemDetail;


